import { Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { NgbModal, NgbDateStruct, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { filter, first, forkJoin, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, shareReplay, switchMap, timeout } from 'rxjs/operators';
import { CustomerService } from '../../../services/customers/customer.service';
import { Customer } from '../../../models/customer';
import { CustomerProductPrice } from '../../../models/price';
import { PriceService } from '../../../services/price/price.service';
import { Product } from '../../../models/product';
import { Availability } from '../../../models/availability';
import { AvailabilityService } from '../../../services/availability/availability.service';
import { AuthService } from '../../../services/auth/auth.service';
import { GungFlowService } from '../../../services/gung-flow/gung-flow.service';
import { User } from '../../../state/auth/types';
import { UsersService } from '../../../services/users/users.service';
import { CheckoutRowStockService } from '../../../services/checkout-row-stock/checkout-row-stock.service';
import { CheckoutQuantityValidationService } from '../../../services/quantity-validation/checkout-quantity-validation.service';
import { ValidationResult } from '../../../models/quantity-validation';
import { PdfAiParserService, PdfAiParserResponse, ParsedOrderRow } from '../../../services/pdf-ai-parser/pdf-ai-parser.service';
import {
  AiOrderParserCodedField,
  AiOrderParserReviewService
} from '../../../services/ai-order-parser-review/ai-order-parser-review.service';
import { MetadataService } from '../../../services/metadata/metadata.service';
import {
  AiParserDeliveryLocation,
  CustomerDeliveryLocationsService
} from '../../../services/customer-delivery-locations/customer-delivery-locations.service';
import { AiOrderParserConfig } from '../../../models/ai-order-parser-config';
import { AiOrderParserSettings } from '../../../models/ai-order-parser-settings';
import {
  AiOrderParserItem,
  AiOrderParserTrustCounter,
  AiOrderParserTrustStatus
} from '../../../models/ai-order-parser-item';
import { AiOrderParserWarning } from '../../../models/ai-order-parser-warning';
import { Order } from '../../../models/order';
import { OrderRow } from '../../../models/orderRow';
import { PdfAiParserSaveConfigModalComponent } from '../pdf-ai-parser-save-config-modal/pdf-ai-parser-save-config-modal.component';
import {
  AiOrderParserPriceDeviation,
  AiOrderParserReceiptRow,
  AiOrderParserSubmitField,
  AiOrderParserSubmitSummary,
  PdfAiParserConfirmSubmitModalComponent
} from '../pdf-ai-parser-confirm-submit-modal/pdf-ai-parser-confirm-submit-modal.component';
import { CanDeactivateComponent } from '../../can-deactivate.component';
import { GungNotificationService } from 'gung-common';
import { TranslateService } from '@ngx-translate/core';
import {
  AI_PARSER_ROW_FAULT,
  AI_PARSER_ROW_FAULT_KEYS,
  AI_PARSER_UNOVERRIDABLE_FAULTS,
  AI_PARSER_ROW_FAULT_FIELDS,
  AiParserAvailabilityState,
  computeAvailabilityState,
  computeRowFaults
} from './ai-parser-row-validity';

/** A control that can carry a fault message in the rows table. */
export type AiParserFaultFieldSlot = 'product' | 'quantity' | 'docPrice' | 'custPrice';

export type AiParserFaultSeverity = 'error' | 'warning';

export interface EditableOrderRow extends ParsedOrderRow {
  isValid?: boolean;
  validationErrors?: string[];
  validationIssues?: string[];
  /** Raw fault codes from the last computeRowFaults() run (AI_PARSER_ROW_FAULT values), not the translated strings in validationErrors. */
  faultCodes?: string[];
  /** True once a product lookup has answered for the id currently in this row. */
  productLookupDone?: boolean;
  product?: Product;
  /** Unit price as the AI read it from the document; undefined when the document showed none. */
  documentUnitPrice?: number;
  /** EAN the row's product was identified by (AI at parse, or the backend resolving a typed EAN); cleared when the reviewer changes the product. */
  ean?: string;
  /** Customer's unit price from Gung's standard price logic; null when no price could be resolved.
   *  Never written to by the reviewer — a manual edit goes to manualUnitPrice instead, so this stays
   *  Gung's own record of the customer's real price, untouched by what gets charged on this order. */
  customerUnitPrice?: number | null;
  /** Reviewer's manual override for this row's price on this order only — never fed back into
   *  customerUnitPrice or any customer-price record. Set together with priceChoice = 'manual'. */
  manualUnitPrice?: number | null;
  /** Currency of customerUnitPrice. */
  currencyCode?: string;
  /** True when the document price and the customer price differ (both rounded down to one decimal). */
  priceDeviates?: boolean;
  /**
   * True when the document carried no unit price but the customer has one. Nothing is wrong with the
   * row, but the reviewer has to say so before it is posted, so it needs a stance exactly like a
   * deviating row does.
   */
  priceMissing?: boolean;
  /** The user's stance on a deviating row; every deviating row needs one before submit / send to cart. */
  priceChoice?: 'document' | 'customer' | 'manual' | null;
  /** Current stock for the indicator under the qty input; fetched in review mode, never persisted. */
  availability?: Availability;
  /** The parsed row this one was built from (absent for added rows); never persisted. */
  sourceRow?: OrderRow;
}

/**
 * Price deviation rule: round both prices down to the first decimal and compare.
 * The scaled values are epsilon-rounded first so float noise (0.07 * 10 = 0.7000...01)
 * cannot flip the floor.
 */
export function pricesDeviate(documentPrice: number, customerPrice: number): boolean {
  const floorOneDecimal = (value: number) => Math.floor(Number((value * 10).toFixed(6)));
  return floorOneDecimal(documentPrice) !== floorOneDecimal(customerPrice);
}

/** Where the value of an editable customer field came from. */
export type CustomerFieldSource = 'config' | 'ai' | 'manual';

/**
 * One order-metadata field: the GME property path under `order.extra.gme` — the same key
 * `/coded-fields`, `discardedMetadata` and `deviatingMetadata` are all keyed by — and its label.
 */
export interface AiParserMetadataField {
  property: string;
  labelKey: string;
  /** Spans both grid columns instead of taking one. */
  wide?: boolean;
  /** Rendered as a textarea rather than a single-line input. */
  multiline?: boolean;
  /** Shown only as a master-data dropdown; an installation that codes nothing gets no field. */
  codedOnly?: boolean;
  /** Shown only when this installation maps it to an ERP field (personal data, so opt-in). */
  mappedOnly?: boolean;
}

// One flat list per tab, laid out by the grid in reading order — two independent columns paired
// fields that have nothing to do with each other ("Payment terms" beside "Shipping agent").
const AI_PARSER_METADATA_HEADER: AiParserMetadataField[] = [
  { property: 'termsOfPayment', labelKey: 'AI_PARSER_METADATA_PAYMENT_TERMS' },
  { property: 'deliveryMethod', labelKey: 'AI_PARSER_METADATA_DELIVERY_METHOD' },
  { property: 'salesperson', labelKey: 'AI_PARSER_METADATA_SALESPERSON' },
  { property: 'shippingAgent', labelKey: 'AI_PARSER_METADATA_SHIPPING_AGENT' },
  { property: 'customerOrderId', labelKey: 'AI_PARSER_METADATA_REFERENCE_NO' },
  { property: 'externalReference', labelKey: 'AI_PARSER_METADATA_YOUR_REFERENCE', mappedOnly: true },
  { property: 'externalText', labelKey: 'AI_PARSER_METADATA_MESSAGE', wide: true, multiline: true }
];

/** The address reads as an address: the name spans the row, then street, then zip/city, then country. */
const AI_PARSER_METADATA_ADDRESS: AiParserMetadataField[] = [
  { property: 'deliveryAddress.name', labelKey: 'AI_PARSER_METADATA_ADDRESS_NAME', wide: true },
  { property: 'deliveryAddress.address1', labelKey: 'AI_PARSER_METADATA_ADDRESS1' },
  { property: 'deliveryAddress.address2', labelKey: 'AI_PARSER_METADATA_ADDRESS2' },
  { property: 'deliveryAddress.zipCode', labelKey: 'AI_PARSER_METADATA_ZIP_CODE' },
  { property: 'deliveryAddress.city', labelKey: 'AI_PARSER_METADATA_CITY' },
  { property: 'deliveryAddress.countryCode', labelKey: 'AI_PARSER_METADATA_COUNTRY' }
];

/** Shown under the rows table, beside the save button, rather than among the header fields. */
const AI_PARSER_METADATA_STOCK: AiParserMetadataField = {
  property: 'stockId',
  labelKey: 'AI_PARSER_METADATA_STOCK_LOCATION',
  codedOnly: true
};

const AI_PARSER_METADATA_FIELDS: AiParserMetadataField[] = [
  ...AI_PARSER_METADATA_HEADER,
  ...AI_PARSER_METADATA_ADDRESS,
  AI_PARSER_METADATA_STOCK
];

/** One sheet of an Excel/CSV file rendered as a table in the document preview pane. */
export interface SpreadsheetPreviewSheet {
  name: string;
  /** Formatted cell values for the whole sheet (first row is rendered as the header). */
  rows: string[][];
  totalRowCount: number;
  /** How many rows are currently rendered; grown in steps via the "show more rows" button. */
  visibleCount: number;
}

const SPREADSHEET_PREVIEW_INITIAL_ROWS = 100;
const SPREADSHEET_PREVIEW_ROWS_STEP = 100;
/** How often the review page asks whether a background parse has finished. */
const PARSE_POLL_MS = 3000;
const SPREADSHEET_EXTENSIONS = ['.xlsx', '.xls', '.csv'];
const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp'];

function isSpreadsheetFileName(name: string | undefined): boolean {
  const lower = (name || '').toLowerCase();
  return SPREADSHEET_EXTENSIONS.some(ext => lower.endsWith(ext));
}

function isImageFile(type: string | undefined, name: string | undefined): boolean {
  if (type?.startsWith('image/')) {
    return true;
  }
  const lower = (name || '').toLowerCase();
  return IMAGE_EXTENSIONS.some(ext => lower.endsWith(ext));
}

@Component({
  selector: 'lib-pdf-ai-parser-view',
  standalone: false,
  templateUrl: './pdf-ai-parser-view.component.html',
  styleUrls: ['./pdf-ai-parser-view.component.scss']
})
export class PdfAiParserViewComponent extends CanDeactivateComponent implements OnInit, OnDestroy {
  private translateService = inject(TranslateService);
  private authService = inject(AuthService);
  private checkoutRowStockService = inject(CheckoutRowStockService);
  private checkoutQuantityValidationService = inject(CheckoutQuantityValidationService);
  private gungFlowService = inject(GungFlowService);
  private usersService = inject(UsersService);

  // Reviewed order (uploaded manually or received by email) state
  @ViewChild('rejectModal') rejectModalTpl!: TemplateRef<unknown>;
  @ViewChild('deleteConfigModal') deleteConfigModalTpl!: TemplateRef<unknown>;
  @ViewChild('resolveOutcomeModal') resolveOutcomeModalTpl!: TemplateRef<unknown>;
  reviewItemId: string | null = null;
  reviewItem: AiOrderParserItem | null = null;
  rejectionReason = '';
  resolveErpOrderId = '';
  isSubmittingToErp = false;
  isRejecting = false;
  isResolvingOutcome = false;
  /** Fetching customer + prices for the submit confirmation dialog. */
  isPreparingSubmit = false;
  /** Set when the order was submitted to the ERP in this session, to show the created order number. */
  justSubmittedToErp = false;

  pdfUrl: SafeResourceUrl | null = null;
  private rawPdfUrl: string | null = null;
  /** The PDF attachment itself, read for the sender email suggestions. */
  private pdfBlob: Blob | null = null;
  /** Preview of a photographed/scanned order image; null when not applicable. */
  imageUrl: SafeUrl | null = null;
  private rawImageUrl: string | null = null;
  /** Tabular preview of an Excel/CSV file, one entry per sheet; null when not applicable. */
  spreadsheetPreview: SpreadsheetPreviewSheet[] | null = null;
  private spreadsheetPreviewLoad: Promise<void> = Promise.resolve();
  activePreviewSheetIndex = 0;
  isPreviewLoading = false;
  prompt = '';
  /** Free text for this one order, seeded server-side from the mail body; sent only on opt-in. */
  manualInput = '';
  useManualInput = false;
  deliveryCustomerId = '';
  billingCustomerId = '';
  /** Comma-separated sender addresses/domains that auto-select the config for emailed orders. */
  senderEmails = '';

  configs: AiOrderParserConfig[] = [];
  selectedConfigName = '';
  isLoadingConfigs = false;
  isSavingConfig = false;

  /** Resolved names for the customer ids in the Parameters fields; null while loading/empty, '' when not found. */
  deliveryCustomerName: string | null = null;
  billingCustomerName: string | null = null;
  /** Resolved name for the editable order's delivery customer; null while loading/empty, '' when not found. */
  editableDeliveryCustomerName: string | null = null;
  private customerNameCache = new Map<string, string>();
  private deliveryNameLookup$ = new Subject<string>();
  private billingNameLookup$ = new Subject<string>();
  /** Re-prices the rows after the editable delivery customer was edited (debounced while typing). */
  private rowPriceReload$ = new Subject<void>();
  /** Re-resolves product/name/availability and revalidates every row after a manual product ID edit (debounced while typing). */
  private rowProductIdChange$ = new Subject<void>();
  private nameLookupSubs = new Subscription();

  deliveryCustomerIdSource: CustomerFieldSource | null = null;
  billingCustomerIdSource: CustomerFieldSource | null = null;
  deliveryDateSource: CustomerFieldSource | null = null;
  /** The customer ids as the AI extracted them, before any config fill or manual edit. */
  private aiDeliveryCustomerId = '';
  private aiBillingCustomerId = '';
  private aiDeliveryDate = '';

  private loadedAiInstructions = '';
  private loadedDeliveryCustomerId = '';
  private loadedBillingCustomerId = '';
  private loadedSenderEmails = '';

  isLoading = false;
  isValidating = false;
  /** A validate was asked for while one was in flight; it runs once that one completes. */
  private revalidateQueued = false;
  isValidated = false;
  hasPendingChanges = false;
  /** Bumped on every edit, so a validate response can tell whether it still describes the rows. */
  editVersion = 0;
  pricesResolved = false;
  /** Fetching customer prices for the rows table's price comparison. */
  isLoadingPrices = false;

  /**
   * Backend message from a failed parse attempt, rendered as a danger note in the
   * setup panel with a "Try again" action. Kept apart from errorMessage so other
   * errors (load/save/submit) still surface through the top-level note.
   */
  parseErrorMessage: string | null = null;
  /**
   * Translate key of the current parse-theater status line; non-null only while a
   * parse is running (drives the progress bar, skeleton table and preview dim).
   */
  parsePhaseKey: string | null = null;
  private parsePhaseTimer: ReturnType<typeof setInterval> | null = null;
  private parseSub?: Subscription;
  /** Iteration helper for the parse-theater skeleton table. */
  readonly skeletonRows = Array.from({ length: 6 });
  /** After a successful parse the setup panel collapses to a one-line summary. */
  setupCollapsed = false;
  /** "Automatic matching" disclosure for the sender-emails field; auto-opens when a config carries values. */
  senderMatchingOpen = false;
  /** Manual input starts collapsed; it opens by itself only when the last parse actually used it. */
  manualInputOpen = false;
  /** Order details start closed; they open by themselves only when something in them needs a decision. */
  orderMetadataOpen = false;
  /** Trust card body starts collapsed to save vertical space. */
  trustCardCollapsed = true;

  /**
   * The setup panel only has a summary to collapse back to once a parse produced an
   * order; before that the body is the only content and must stay expanded.
   */
  get canCollapseSetup(): boolean {
    return !!this.response?.order;
  }

  response: PdfAiParserResponse | null = null;
  editableRows: EditableOrderRow[] = [];
  editableDeliveryCustomerId = '';
  editableBillingCustomerId = '';
  /** Requested delivery date as yyyy-MM-dd, stored ERP-agnostically in order.extra.gme.deliveryDate. */
  editableDeliveryDate = '';
  /** Typed delivery date text that does not parse; blocks save and submit until fixed or cleared. */
  deliveryDateInvalid = false;

  // --- Order metadata (order.extra.gme header + delivery address) ---

  readonly metadataHeaderFields = AI_PARSER_METADATA_HEADER;
  readonly metadataStockField = AI_PARSER_METADATA_STOCK;
  readonly metadataAddressFields = AI_PARSER_METADATA_ADDRESS;

  /** The delivery customer's registered delivery locations; empty means no picker is offered. */
  deliveryLocations: AiParserDeliveryLocation[] = [];
  /** The picked location, kept only so the select shows it; nothing is ever saved back. */
  /** The address fieldset stays locked until the reviewer opts in, as in the checkout. */
  editDeliveryAddress = false;

  /** Editable order metadata, flat by GME property path (`deliveryAddress.city` and friends). */
  orderMetadata: { [property: string]: string } = {};
  /** The same values as the AI extracted them, for the provenance glyphs. */
  private aiOrderMetadata: { [property: string]: string } = {};
  /** orderMetadata as last loaded or saved; a change only counts as an edit when it differs from this. */
  private metadataBaseline: { [property: string]: string } = {};
  /** Which properties this installation validates against master data; empty until loaded. */
  codedFields: { [property: string]: AiOrderParserCodedField } = {};
  /** The installation maps the sender email (extra.aiParser.senderEmail); the field is hidden otherwise. */
  senderEmailEnabled = false;
  /** The installation maps the buyer's contact person (gme.externalReference); the field is hidden otherwise. */
  externalReferenceEnabled = false;
  senderEmail = '';
  /** "Other…" picked: the reviewer types an address that is in none of the suggestions. */
  senderEmailOther = false;
  readonly senderEmailOtherValue = '__other__';
  /** Addresses found in the order document's text; best effort, empty when nothing could be read. */
  documentEmails: string[] = [];
  /** The reviewer's per-order stance on deviating order metadata. Never persisted, never pre-ticked. */
  confirmOrderMetadataDeviations = false;
  /** The reviewer accepts an address the customer has never registered. Reset wherever that one is. */
  confirmUnregisteredAddress = false;
  /** The reviewer accepts the configuration's customer over the one the document named. */
  confirmCustomerOverride = false;
  errorMessage = '';
  validRowCount = 0;
  invalidRowCount = 0;
  /** Mirrors AiOrderParserSettings.allowInvalidRowOverride; loaded in review mode. */
  allowInvalidRowOverride = false;
  /** The reviewer's per-order decision to submit anyway. Never persisted, never pre-ticked. */
  overrideInvalidRows = false;
  /**
   * Stock column / stock check, as web checkout decides them from the tenant's default flow, both
   * switched off by AiOrderParserSettings.checkAvailability. Loaded in review mode.
   */
  showStock = false;
  checkStock = false;
  /** The stock used while the order names none, never saved; see resolveDefaultStockId. */
  defaultStockId?: string;

  /** The order's stock location (extra.gme.stockId), else the default stock. */
  get stockId(): string | undefined {
    return this.orderMetadata['stockId'] || this.defaultStockId;
  }

  constructor(
    private pdfAiParserService: PdfAiParserService,
    private reviewService: AiOrderParserReviewService,
    private sanitizer: DomSanitizer,
    private customerService: CustomerService,
    protected availabilityService: AvailabilityService,
    private priceService: PriceService,
    private metadataService: MetadataService,
    private deliveryLocationsService: CustomerDeliveryLocationsService,
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private router: Router,
    private gungNotificationService: GungNotificationService
  ) {
    super();
  }

  canDeactivate(): boolean {
    // Manual input is parse-time input with no save path of its own, so the honest warning is
    // "you will lose this unless you parse". Kept out of hasUnsavedConfigChanges: the template
    // reads that getter for the config-save button, and manual input is not config.
    // hasPendingChanges covers order edits (rows, customers, metadata) not yet saved by validate.
    return !(
      this.hasUnsavedConfigChanges
      || this.hasPendingChanges
      || this.manualInput !== (this.reviewItem?.manualInput || '')
    );
  }

  get hasUnsavedConfigChanges(): boolean {
    // Deliberately NOT gated on selectedConfigName: with "No configuration" picked the baselines
    // are all empty (snapshotLoaded(null)), so typed instructions or customer ids still count as
    // unsaved work. Gating on it silently threw away exactly the setup a first-time parse needs.
    return this.prompt !== this.loadedAiInstructions
      || this.deliveryCustomerId !== this.loadedDeliveryCustomerId
      || this.billingCustomerId !== this.loadedBillingCustomerId
      || this.senderEmails !== this.loadedSenderEmails;
  }

  private snapshotLoaded(config: AiOrderParserConfig | null): void {
    this.loadedAiInstructions = config?.aiInstructions || '';
    this.loadedDeliveryCustomerId = config?.suggestedDeliveryCustomerId || '';
    this.loadedBillingCustomerId = config?.suggestedBillingCustomerId || '';
    this.loadedSenderEmails = (config?.senderEmails || []).join(', ');
  }

  /** Parse the comma/newline-separated sender emails field into the config's list form. */
  private senderEmailsAsList(): string[] | undefined {
    const entries = this.senderEmails
      .split(/[,\n;]/)
      .map(entry => entry.trim())
      .filter(entry => !!entry);
    return entries.length > 0 ? entries : undefined;
  }

  ngOnInit(): void {
    this.nameLookupSubs.add(
      this.deliveryNameLookup$.pipe(debounceTime(400)).subscribe(id => this.lookupParamCustomerName(id, 'delivery'))
    );
    this.nameLookupSubs.add(
      this.billingNameLookup$.pipe(debounceTime(400)).subscribe(id => this.lookupParamCustomerName(id, 'billing'))
    );
    this.nameLookupSubs.add(
      this.rowPriceReload$.pipe(debounceTime(600)).subscribe(() => this.reloadPricesForEditedCustomer())
    );
    this.nameLookupSubs.add(
      // Re-price, not just re-validate: a different article has a different customer price, and
      // loadRowPrices() ends in recomputeRowValidity() either way.
      this.rowProductIdChange$.pipe(debounceTime(500)).subscribe(() => {
        this.loadRowProducts();
        this.loadRowPrices();
      })
    );

    // The view is always opened on a stored item; configs are loaded with it.
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      return;
    }
    this.reviewItemId = id;
    forkJoin({
      item: this.loadReviewItem(id),
      // Nothing configured (or the call failing) simply leaves every metadata field as free text.
      codedFields: this.reviewService.getCodedFields().pipe(first(), catchError(() => of({}))),
      senderEmailEnabled: this.reviewService.getSenderEmailEnabled().pipe(first()),
      externalReferenceEnabled: this.reviewService.getExternalReferenceEnabled().pipe(first()),
      settings: this.pdfAiParserService.getSettings().pipe(first()),
      flow: this.gungFlowService.getDefaultGungFlow().pipe(first()),
      user: this.authService.getCurrentUser().pipe(first())
    }).pipe(
      switchMap(loaded => {
        this.codedFields = loaded.codedFields || {};
        this.senderEmailEnabled = loaded.senderEmailEnabled;
        this.externalReferenceEnabled = loaded.externalReferenceEnabled;
        if (this.senderEmailEnabled) {
          void this.loadDocumentEmails();
        }
        return this.resolveDefaultStockId(loaded.item, loaded.settings, loaded.user).pipe(
          map(defaultStockId => ({ ...loaded, defaultStockId }))
        );
      })
    ).subscribe({
      next: ({ settings, flow, defaultStockId }) => {
        this.allowInvalidRowOverride = !!settings?.allowInvalidRowOverride;
        this.showStock = !!settings?.checkAvailability && !!(flow?.useAvailabilities || flow?.requireAvailability);
        this.checkStock = !!settings?.checkAvailability
          && !!(flow?.requireAvailability || flow?.requireCurrentAvailability || flow?.disableNonAvailable);
        this.defaultStockId = defaultStockId;
        // Settings can resolve after the item's first validity pass; recompute so a
        // late-arriving allowInvalidRowOverride is reflected in row state at once.
        if (this.editableRows.length > 0) {
          this.recomputeRowValidity();
        }
        this.loadRowAvailabilities();
      },
      error: () => {
        this.allowInvalidRowOverride = false;
        this.showStock = false;
        this.checkStock = false;
      }
    });
  }

  onConfigSelected(name: string, applyCustomerIds = true): void {
    this.selectedConfigName = name;
    const config = name ? this.configs.find(c => c.name === name) : undefined;
    if (!config) {
      this.clearParams();
      this.snapshotLoaded(null);
      this.refreshParamCustomerNames();
      this.recomputeCustomerSources();
      return;
    }
    this.prompt = config.aiInstructions || '';
    this.deliveryCustomerId = config.suggestedDeliveryCustomerId || '';
    this.billingCustomerId = config.suggestedBillingCustomerId || '';
    this.senderEmails = (config.senderEmails || []).join(', ');
    if (this.senderEmails) {
      this.senderMatchingOpen = true;
    }
    this.snapshotLoaded(config);
    this.refreshParamCustomerNames();
    if (applyCustomerIds) {
      this.applyConfigCustomerIds(config);
    } else {
      this.recomputeCustomerSources();
    }
  }

  get selectedConfig(): AiOrderParserConfig | null {
    return this.configs.find(c => c.name === this.selectedConfigName) || null;
  }

  /** Called as the user types in a Parameters customer id field; lookup is debounced via the subjects. */
  onParamCustomerIdChanged(field: 'delivery' | 'billing'): void {
    if (field === 'delivery') {
      this.deliveryCustomerName = null;
      this.deliveryNameLookup$.next(this.deliveryCustomerId);
    } else {
      this.billingCustomerName = null;
      this.billingNameLookup$.next(this.billingCustomerId);
    }
  }

  /** preventDefault because ngbTypeahead would otherwise write the whole Customer into the field. */
  onParamCustomerPicked(field: 'delivery' | 'billing', event: NgbTypeaheadSelectItemEvent<Customer>): void {
    event.preventDefault();
    if (field === 'delivery') {
      this.deliveryCustomerId = event.item.id;
    } else {
      this.billingCustomerId = event.item.id;
    }
    this.onParamCustomerIdChanged(field);
  }

  /** Resolve names for the current Parameters field values immediately (config selection, load). */
  private refreshParamCustomerNames(): void {
    this.deliveryCustomerName = null;
    this.billingCustomerName = null;
    this.lookupParamCustomerName(this.deliveryCustomerId, 'delivery');
    this.lookupParamCustomerName(this.billingCustomerId, 'billing');
  }

  private lookupParamCustomerName(customerId: string, field: 'delivery' | 'billing'): void {
    if (!customerId) {
      return;
    }
    this.lookupCustomerName(customerId, name => {
      // Only apply if the field still holds the id we looked up
      const current = field === 'delivery' ? this.deliveryCustomerId : this.billingCustomerId;
      if (current !== customerId) {
        return;
      }
      if (field === 'delivery') {
        this.deliveryCustomerName = name;
      } else {
        this.billingCustomerName = name;
      }
    });
  }

  private lookupCustomerName(customerId: string, assign: (name: string) => void): void {
    if (this.customerNameCache.has(customerId)) {
      assign(this.customerNameCache.get(customerId)!);
      return;
    }
    this.customerService.getCustomer(customerId).pipe(first()).subscribe({
      next: customer => {
        const name = customer?.name || '';
        this.customerNameCache.set(customerId, name);
        assign(name);
      },
      error: () => {
        this.customerNameCache.set(customerId, '');
        assign('');
      }
    });
  }

  /** Fill the editable order's customer ids from the config's saved customer numbers. */
  private applyConfigCustomerIds(config: AiOrderParserConfig): void {
    const previousDeliveryId = this.editableDeliveryCustomerId;
    const previousBillingId = this.editableBillingCustomerId;
    if (config.suggestedDeliveryCustomerId) {
      this.editableDeliveryCustomerId = config.suggestedDeliveryCustomerId;
    }
    if (config.suggestedBillingCustomerId) {
      this.editableBillingCustomerId = config.suggestedBillingCustomerId;
    }
    if (this.editableDeliveryCustomerId !== previousDeliveryId) {
      this.onEditableDeliveryCustomerIdChanged();
    } else {
      this.recomputeCustomerSources();
      if (this.editableBillingCustomerId !== previousBillingId) {
        this.hasPendingChanges = true;
        this.editVersion++;
      }
    }
    this.refreshEditableDeliveryCustomerName();
  }

  /**
   * Derive where each editable customer id came from by comparing the current
   * value against the selected config's saved number and the AI-extracted
   * original. Config wins ties since its suggestion is what drove the value.
   */
  recomputeCustomerSources(): void {
    const config = this.selectedConfig;
    this.deliveryCustomerIdSource = this.resolveSource(
      this.editableDeliveryCustomerId,
      config?.suggestedDeliveryCustomerId,
      this.aiDeliveryCustomerId
    );
    this.billingCustomerIdSource = this.resolveSource(
      this.editableBillingCustomerId,
      config?.suggestedBillingCustomerId,
      this.aiBillingCustomerId
    );
    this.deliveryDateSource = this.resolveSource(this.editableDeliveryDate, undefined, this.aiDeliveryDate);
  }

  private resolveSource(value: string, configValue: string | undefined, aiValue: string): CustomerFieldSource | null {
    if (!value) {
      return null;
    }
    if (configValue && value === configValue) {
      return 'config';
    }
    if (aiValue && value === aiValue) {
      return 'ai';
    }
    return 'manual';
  }

  private clearParams(): void {
    this.prompt = '';
    this.deliveryCustomerId = '';
    this.billingCustomerId = '';
    this.senderEmails = '';
  }

  saveConfig(): void {
    if (!this.selectedConfigName) {
      return;
    }
    // Start from the saved config: the backend replaces the document whole, so fields this page
    // does not show (delivery-address switch, responsible user, auto-submit) would otherwise be reset.
    const existing = this.configs.find(c => c.name === this.selectedConfigName);
    this.persistConfig({
      ...existing,
      name: this.selectedConfigName,
      aiInstructions: this.prompt || undefined,
      senderEmails: this.senderEmailsAsList(),
      suggestedDeliveryCustomerId: this.deliveryCustomerId || undefined,
      suggestedBillingCustomerId: this.billingCustomerId || undefined
    });
  }

  createConfig(): void {
    if (this.selectedConfigName) {
      this.selectedConfigName = '';
      this.clearParams();
      this.snapshotLoaded(null);
    }

    const draft: AiOrderParserConfig = {
      name: '',
      aiInstructions: this.prompt || undefined,
      senderEmails: this.senderEmailsAsList(),
      suggestedDeliveryCustomerId: this.deliveryCustomerId || undefined,
      suggestedBillingCustomerId: this.billingCustomerId || undefined
    };

    const modalRef = this.modalService.open(PdfAiParserSaveConfigModalComponent, {
      centered: true
    });
    modalRef.componentInstance.draft = draft;
    modalRef.componentInstance.existingNames = this.configs.map(c => c.name);
    modalRef.componentInstance.name = '';

    modalRef.result.then(
      (result: AiOrderParserConfig) => {
        this.persistConfig(result);
      },
      () => {
        // dismissed
      }
    );
  }

  private persistConfig(payload: AiOrderParserConfig): void {
    this.isSavingConfig = true;
    this.pdfAiParserService.saveConfig(payload).pipe(first()).subscribe({
      next: (saved) => {
        const idx = this.configs.findIndex(c => c.name === saved.name);
        this.configs = idx >= 0
          ? this.configs.map(c => (c.name === saved.name ? saved : c))
          : [...this.configs, saved];
        this.snapshotLoaded(saved);
        this.isSavingConfig = false;
        this.gungNotificationService.notify(
          this.translateService.instant('AI_PARSER_TOAST_CONFIG_SAVED_TITLE'),
          this.translateService.instant('AI_PARSER_TOAST_CONFIG_SAVED_BODY', { name: saved.name }),
          'success',
          undefined,
          4000
        );
        // Defer to next tick so the new <option> is rendered before ngModel binds
        setTimeout(() => {
          this.onConfigSelected(saved.name, false);
        });
      },
      error: () => {
        this.errorMessage = this.translateService.instant('AI_PARSER_ERROR_SAVE_CONFIG');
        this.isSavingConfig = false;
      }
    });
  }

  deleteSelectedConfig(): void {
    if (!this.selectedConfigName) {
      return;
    }
    this.modalService.open(this.deleteConfigModalTpl, { centered: true }).result.then(
      () => this.performDeleteConfig(),
      () => { /* dismissed */ }
    );
  }

  private performDeleteConfig(): void {
    const name = this.selectedConfigName;
    if (!name) {
      return;
    }
    const selected = this.configs.find(c => c.name === name);
    if (!selected?.id) {
      return;
    }
    this.pdfAiParserService.deleteConfig(selected.id).pipe(first()).subscribe({
      next: () => {
        this.configs = this.configs.filter(c => c.name !== name);
        this.selectedConfigName = '';
        this.recomputeCustomerSources();
      },
      error: () => {
        this.errorMessage = this.translateService.instant('AI_PARSER_ERROR_DELETE_CONFIG');
      }
    });
  }

  /** Revoke the PDF/image object URLs and drop any spreadsheet preview. */
  private clearFilePreview(): void {
    if (this.rawPdfUrl) {
      URL.revokeObjectURL(this.rawPdfUrl);
      this.rawPdfUrl = null;
      this.pdfUrl = null;
    }
    if (this.rawImageUrl) {
      URL.revokeObjectURL(this.rawImageUrl);
      this.rawImageUrl = null;
      this.imageUrl = null;
    }
    this.spreadsheetPreview = null;
    this.activePreviewSheetIndex = 0;
    this.isPreviewLoading = false;
  }

  /**
   * Parse an Excel/CSV file in the browser and map each sheet to a row matrix for the
   * preview pane. SheetJS is loaded on demand so it stays out of the initial bundle.
   * The preview is best-effort: on parse failure the pane falls back to the
   * "no preview" placeholder instead of surfacing an error.
   */
  private async generateSpreadsheetPreview(data: Blob): Promise<void> {
    this.isPreviewLoading = true;
    try {
      const XLSX = await import('xlsx');
      const workbook = XLSX.read(await data.arrayBuffer(), { type: 'array' });
      const sheets = workbook.SheetNames.map(name => {
        const rows = XLSX.utils.sheet_to_json(workbook.Sheets[name], {
          header: 1,
          defval: '',
          blankrows: false,
          raw: false
        }) as unknown[][];
        return {
          name,
          totalRowCount: rows.length,
          rows: rows.map(row => row.map(cell => (cell == null ? '' : String(cell)))),
          visibleCount: Math.min(SPREADSHEET_PREVIEW_INITIAL_ROWS, rows.length)
        };
      });
      this.spreadsheetPreview = sheets.some(s => s.rows.length > 0) ? sheets : null;
    } catch {
      this.spreadsheetPreview = null;
    } finally {
      this.isPreviewLoading = false;
    }
  }

  /** The mail's sender candidates, then the document's addresses, each address once whatever its case. */
  get senderEmailOptions(): string[] {
    const seen = new Set<string>();
    return [...(this.reviewItem?.senderEmailCandidates || []), ...this.documentEmails].filter(
      email => !seen.has(email.toLowerCase()) && !!seen.add(email.toLowerCase())
    );
  }

  /** The suggestions plus the current value, so an address outside them still shows as selected. */
  get senderEmailSelectOptions(): string[] {
    const options = this.senderEmailOptions;
    return this.senderEmail && !this.senderEmailOther && !options.includes(this.senderEmail)
      ? [...options, this.senderEmail]
      : options;
  }

  onSenderEmailSelected(value: string): void {
    this.senderEmailOther = value === this.senderEmailOtherValue;
    if (!this.senderEmailOther) {
      this.senderEmail = value;
    }
    this.hasPendingChanges = true;
    this.editVersion = this.editVersion + 1;
  }

  /** Read the addresses out of the PDF's text or the spreadsheet preview's cells; any failure leaves none. */
  private async loadDocumentEmails(): Promise<void> {
    try {
      let text = '';
      if (this.pdfBlob) {
        const { getDocument, GlobalWorkerOptions, version } = await import('pdfjs-dist/legacy/build/pdf.mjs');
        GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${version}/legacy/build/pdf.worker.min.mjs`;
        const pdf = await getDocument({ data: await this.pdfBlob.arrayBuffer() }).promise;
        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
          const content = await (await pdf.getPage(pageNumber)).getTextContent();
          text += content.items.map(item => ('str' in item ? item.str + (item.hasEOL ? ' ' : '') : '')).join('') + ' ';
        }
        void pdf.destroy();
      } else {
        await this.spreadsheetPreviewLoad;
        text = (this.spreadsheetPreview || []).map(sheet => sheet.rows.map(row => row.join(' ')).join(' ')).join(' ');
      }
      this.documentEmails = text.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi) || [];
    } catch {
      this.documentEmails = [];
    }
  }

  get activePreviewSheet(): SpreadsheetPreviewSheet | null {
    return this.spreadsheetPreview?.[this.activePreviewSheetIndex] || null;
  }

  showMoreSpreadsheetRows(sheet: SpreadsheetPreviewSheet): void {
    sheet.visibleCount = Math.min(sheet.visibleCount + SPREADSHEET_PREVIEW_ROWS_STEP, sheet.totalRowCount);
  }

  nextSpreadsheetRowChunk(sheet: SpreadsheetPreviewSheet): number {
    return Math.min(SPREADSHEET_PREVIEW_ROWS_STEP, sheet.totalRowCount - sheet.visibleCount);
  }

  /** True while a parse request is running — drives the parse theater (§0.6). */
  get isParsing(): boolean {
    return this.parsePhaseKey != null;
  }

  /**
   * Start the parse theater: rotating honest status copy keyed off elapsed time
   * (reading → extracting → matching → still-working). The interval is cleared
   * on completion, failure and destroy.
   */
  private startParseTheater(): void {
    this.stopParseTheater();
    const startedAt = Date.now();
    this.parsePhaseKey = 'AI_PARSER_PHASE_READING';
    this.parsePhaseTimer = setInterval(() => {
      const elapsedSeconds = (Date.now() - startedAt) / 1000;
      this.parsePhaseKey =
        elapsedSeconds >= 16
          ? 'AI_PARSER_PHASE_LONG'
          : elapsedSeconds >= 9
            ? 'AI_PARSER_PHASE_MATCHING'
            : elapsedSeconds >= 4
              ? 'AI_PARSER_PHASE_EXTRACTING'
              : 'AI_PARSER_PHASE_READING';
    }, 1000);
  }

  private stopParseTheater(): void {
    if (this.parsePhaseTimer != null) {
      clearInterval(this.parsePhaseTimer);
      this.parsePhaseTimer = null;
    }
    this.parsePhaseKey = null;
  }

  submit(): void {
    this.parseReview();
  }

  ngOnDestroy(): void {
    this.stopParseTheater();
    this.parseSub?.unsubscribe();
    this.nameLookupSubs.unsubscribe();
    if (this.rawPdfUrl) {
      URL.revokeObjectURL(this.rawPdfUrl);
    }
    if (this.rawImageUrl) {
      URL.revokeObjectURL(this.rawImageUrl);
    }
  }

  // --- Review mode (emailed order) ---

  /** Loads the item into the page; the returned item is for whatever waits on it (the default stock). */
  private loadReviewItem(id: string): Observable<AiOrderParserItem> {
    this.isLoading = true;
    const load$ = forkJoin({
      item: this.reviewService.get(id),
      blob: this.reviewService.fetchAttachmentBlob(id),
      configs: this.pdfAiParserService.listConfigs()
    }).pipe(shareReplay(1));
    load$.subscribe({
      next: ({ item, blob, configs }) => {
        this.configs = configs || [];
        this.reviewItem = item;
        this.manualInput = item.manualInput || '';
        // The box reflects what the last parse did, so a re-parse repeats it rather than silently dropping it.
        this.useManualInput = !!item.manualInputUsed;
        this.manualInputOpen = this.useManualInput;
        this.loadTrustCustomerNames();
        this.clearFilePreview();
        if (blob.type === 'application/pdf' || item.originalFilename?.toLowerCase().endsWith('.pdf')) {
          this.rawPdfUrl = URL.createObjectURL(blob);
          this.pdfBlob = blob;
          this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.rawPdfUrl);
        } else if (isImageFile(blob.type, item.originalFilename)) {
          this.rawImageUrl = URL.createObjectURL(blob);
          this.imageUrl = this.sanitizer.bypassSecurityTrustUrl(this.rawImageUrl);
        } else if (isSpreadsheetFileName(item.originalFilename)) {
          this.spreadsheetPreviewLoad = this.generateSpreadsheetPreview(blob);
        }
        this.applyParsedOrder(item.parsedOrder);
        // Preselect the config used for this item and fill the Parameters from it.
        if (item.assignedConfigName) {
          // The backend already applied the config's customers at parse time; re-applying would overwrite the reviewer's saved choice.
          this.onConfigSelected(item.assignedConfigName, false);
        }
        // ERROR items surface their stored message through the finalized outcome note.
        this.isLoading = false;
        // Opened while a parse is still running (another tab, or the page was reloaded mid-parse).
        if (item.status === 'PARSING') {
          this.isLoading = true;
          this.startParseTheater();
          this.watchParse(of(item));
        }
      },
      error: () => {
        this.errorMessage = this.translateService.instant('AI_PARSER_ERROR_LOAD_ORDER');
        this.isLoading = false;
      }
    });
    return load$.pipe(map(({ item }) => item));
  }

  /** The price comparison fields the backend persisted under a row's extra.aiParser. */
  private readAiParserRowExtra(
    extra: Record<string, unknown> | undefined
  ): Pick<EditableOrderRow, 'documentUnitPrice' | 'manualUnitPrice' | 'priceChoice' | 'validationIssues' | 'ean'> {
    const aiParser = ((extra && extra['aiParser']) || {}) as Record<string, unknown>;
    const documentUnitPrice = aiParser['documentUnitPrice'];
    const manualUnitPrice = aiParser['manualUnitPrice'];
    const priceChoice = aiParser['priceChoice'];
    const validationIssues = aiParser['validationIssues'];
    const ean = aiParser['ean'];
    return {
      documentUnitPrice: typeof documentUnitPrice === 'number' ? documentUnitPrice : undefined,
      manualUnitPrice: typeof manualUnitPrice === 'number' ? manualUnitPrice : undefined,
      priceChoice:
        priceChoice === 'document' || priceChoice === 'customer' || priceChoice === 'manual' ? priceChoice : null,
      validationIssues: Array.isArray(validationIssues)
        ? validationIssues.filter((c): c is string => typeof c === 'string')
        : [],
      ean: typeof ean === 'string' && ean ? ean : undefined
    };
  }

  /** Map a backend parsed Order onto the editable view state (rows + customers + result banner). */
  private applyParsedOrder(order?: Order): void {
    this.isValidated = false;
    this.deliveryDateInvalid = false;
    this.hasPendingChanges = false;
    this.pricesResolved = false;
    this.validRowCount = 0;
    this.invalidRowCount = 0;

    if (order && order.rows) {
      this.editableRows = order.rows.map(r => ({
        productId: r.productId,
        quantity: r.quantity,
        extra: r.extra || undefined,
        sourceRow: r,
        ...this.readAiParserRowExtra(r.extra)
      } as EditableOrderRow));
      this.editableDeliveryCustomerId = order.deliveryCustomerId || '';
      this.editableBillingCustomerId = order.billingCustomerId || '';
      this.aiDeliveryCustomerId = order.deliveryCustomerId || '';
      this.aiBillingCustomerId = order.billingCustomerId || '';
      this.editableDeliveryDate = this.extractGmeDeliveryDate(order);
      this.aiDeliveryDate = this.editableDeliveryDate;
      this.orderMetadata = this.extractOrderMetadata(order);
      this.aiOrderMetadata = { ...this.orderMetadata };
      this.metadataBaseline = { ...this.orderMetadata };
      this.senderEmail = ((order.extra || {})['aiParser'] || {})['senderEmail'] || '';
      this.senderEmailOther = false;
      this.confirmOrderMetadataDeviations = false;
      this.confirmUnregisteredAddress = false;
      this.confirmCustomerOverride = false;
      this.recomputeCustomerSources();
      this.response = {
        order: {
          id: order.id || '',
          deliveryCustomerId: order.deliveryCustomerId,
          billingCustomerId: order.billingCustomerId,
          rows: []
        },
        success: true,
        message: this.reviewItem?.parserMessage || this.translateService.instant('AI_PARSER_PARSED_ORDER'),
        warnings: this.reviewItem?.warnings || []
      };
      this.recomputeRowValidity();
      this.refreshEditableDeliveryCustomerName();
      this.loadRowPrices();
      // Region D: a parsed order collapses the setup panel to its summary line.
      this.setupCollapsed = true;
    } else {
      this.editableRows = [];
      this.editableDeliveryCustomerId = '';
      this.editableBillingCustomerId = '';
      this.aiDeliveryCustomerId = '';
      this.aiBillingCustomerId = '';
      this.editableDeliveryDate = '';
      this.aiDeliveryDate = '';
      this.orderMetadata = this.extractOrderMetadata(undefined);
      this.aiOrderMetadata = { ...this.orderMetadata };
      this.metadataBaseline = { ...this.orderMetadata };
      this.senderEmail = '';
      this.senderEmailOther = false;
      this.confirmOrderMetadataDeviations = false;
      this.confirmUnregisteredAddress = false;
      this.confirmCustomerOverride = false;
      this.recomputeCustomerSources();
      this.refreshEditableDeliveryCustomerName();
      this.response = null;
      this.setupCollapsed = false;
    }
  }

  private parseReview(): void {
    if (!this.reviewItemId) {
      return;
    }
    this.isLoading = true;
    this.errorMessage = '';
    this.parseErrorMessage = null;
    this.startParseTheater();
    // No config is a valid choice: the on-screen values are sent inline so an order from an
    // unknown sender can be parsed — and unsaved edits applied — without saving a config first.
    this.watchParse(
      this.reviewService.parse(
        this.reviewItemId,
        this.selectedConfigName,
        this.inlineParseConfig(),
        this.manualInput,
        this.useManualInput
      )
    );
  }

  /**
   * The backend reads the document in the background and answers with the item in PARSING (a
   * multi-page document outlasts the proxy's 60s), so poll until the status moves on, then show the
   * outcome.
   */
  private watchParse(started: Observable<AiOrderParserItem>): void {
    this.parseSub?.unsubscribe();
    this.parseSub = started
      .pipe(
        switchMap(item =>
          item.status !== 'PARSING'
            ? of(item)
            : timer(PARSE_POLL_MS, PARSE_POLL_MS).pipe(
                switchMap(() => this.reviewService.get(item.id)),
                first(polled => polled.status !== 'PARSING')
              )
        )
      )
      .subscribe({
        next: (item) => {
          this.reviewItem = item;
          this.manualInput = item.manualInput || '';
          this.useManualInput = !!item.manualInputUsed;
          this.applyParsedOrder(item.parsedOrder);
          if (item.status === 'ERROR') {
            this.parseErrorMessage = item.errorMessage || '';
            this.setupCollapsed = false;
          }
          this.stopParseTheater();
          this.isLoading = false;
        },
        error: () => {
          this.parseErrorMessage = '';
          this.stopParseTheater();
          this.isLoading = false;
        }
      });
  }

  /**
   * The setup fields as a transient config for the next parse, so what is on screen is what the
   * parse uses — including edits not yet saved to the selected config. Omitted only when there is
   * nothing to say (no config selected and every field empty), so a parse never overrides a saved
   * config with a blank one.
   */
  private inlineParseConfig(): AiOrderParserConfig | undefined {
    const aiInstructions = (this.prompt || '').trim();
    const deliveryCustomerId = (this.deliveryCustomerId || '').trim();
    const billingCustomerId = (this.billingCustomerId || '').trim();
    if (!this.selectedConfigName && !aiInstructions && !deliveryCustomerId && !billingCustomerId) {
      return undefined;
    }
    // With a config selected the backend overlays these three fields onto the saved config, so an
    // emptied box has to travel as '' to mean "not for this reading"; omitting it would fall back
    // to the saved value and the reviewer's clearing would be silently undone. Without a config
    // there is nothing to fall back to, so empty stays omitted.
    const cleared = this.selectedConfigName ? '' : undefined;
    return {
      name: this.selectedConfigName || '',
      aiInstructions: aiInstructions || cleared,
      suggestedDeliveryCustomerId: deliveryCustomerId || cleared,
      suggestedBillingCustomerId: billingCustomerId || cleared
    };
  }

  /**
   * Build a backend Order from the current editable state, preserving untouched fields of the original.
   * Rows get stockId, stamped exactly as web checkout's AssignOrderRows step stamps its cart's stock.
   */
  private buildReviewOrder(user: User): Order {
    const base = (this.reviewItem?.parsedOrder || {}) as Order;
    // Only extra reaches the backend: its OrderRow has no targetStockId, the ERP field in extra carries the stock.
    const stamped = this.checkoutRowStockService.assignStock(
      this.editableRows.map(r => ({ targetStockId: this.stockId, extra: this.buildRowExtra(r) })),
      user
    );
    return {
      ...base,
      deliveryCustomerId: this.editableDeliveryCustomerId,
      billingCustomerId: this.editableBillingCustomerId,
      extra: this.buildOrderExtra(base),
      rows: this.editableRows.map((r, i) => {
        return {
          // id mirrors productId (as convertToOrder does at parse): several ERP posting steps
          // resolve the product by row.id, so a stale pre-edit id would drop/mispost an edited
          // row. The backend re-asserts this too; keeping it here avoids sending divergent data.
          id: r.productId ?? r.sourceRow?.id ?? '',
          // The parsed name only while the row still holds the product it was parsed with.
          name: r.product
            ? r.product.name
            : r.sourceRow?.productId === r.productId ? r.sourceRow.name ?? '' : '',
          extra: stamped[i].extra,
          orderId: r.sourceRow?.orderId ?? base.id ?? '',
          productId: r.productId,
          quantity: r.quantity
        } as OrderRow;
      })
    };
  }

  /**
   * The order's extra with the reviewed delivery date and order metadata written
   * ERP-agnostically to gme, where the ERP order pipeline picks them up. Clearing a field
   * removes it so the pipeline falls back to its default. Of extra.aiParser only the reviewed
   * senderEmail is written here: the discarded and the deviating signals are the server's own.
   */
  private buildOrderExtra(base: Order): { [key: string]: any } {
    const extra: { [key: string]: any } = { ...(base.extra || {}) };
    const gme: { [key: string]: any } = { ...(extra['gme'] || {}) };
    if (this.editableDeliveryDate) {
      gme['deliveryDate'] = this.editableDeliveryDate;
    } else {
      delete gme['deliveryDate'];
    }
    for (const field of AI_PARSER_METADATA_FIELDS) {
      const [head, tail] = field.property.split('.');
      // Nested properties write into a copy of their parent, so fields this form does not
      // show (an address's gln, say) survive the round trip untouched.
      const target = tail ? (gme[head] = { ...(gme[head] || {}) }) : gme;
      const value = (this.orderMetadata[field.property] || '').trim();
      if (value) {
        target[tail || head] = value;
      } else {
        delete target[tail || head];
      }
    }
    for (const field of AI_PARSER_METADATA_FIELDS) {
      const [head, tail] = field.property.split('.');
      // An all-blank address is no address: the ERP step reads an absent key and an all-null
      // one the same, so a parent left with no keys is dropped instead of stored as {}.
      if (tail && gme[head] && Object.keys(gme[head]).length === 0) {
        delete gme[head];
      }
    }
    extra['gme'] = gme;
    if (this.senderEmailEnabled) {
      const aiParser = { ...(extra['aiParser'] || {}) };
      const senderEmail = this.senderEmail.trim();
      if (senderEmail) {
        aiParser['senderEmail'] = senderEmail;
      } else {
        delete aiParser['senderEmail'];
      }
      extra['aiParser'] = aiParser;
    }
    return extra;
  }

  /** The order metadata under extra.gme, flattened onto the GME property paths the form binds. */
  private extractOrderMetadata(order?: Order): { [property: string]: string } {
    const gme = ((order?.extra || {})['gme'] || {}) as { [key: string]: any };
    const values: { [property: string]: string } = {};
    for (const field of AI_PARSER_METADATA_FIELDS) {
      const [head, tail] = field.property.split('.');
      const raw = tail ? (gme[head] || {})[tail] : gme[head];
      values[field.property] = raw == null ? '' : '' + raw;
    }
    return values;
  }

  /** The server-written order-metadata signals, read off the item so a save refreshes them. */
  private get aiParserOrderExtra(): { [key: string]: any } {
    return ((this.reviewItem?.parsedOrder?.extra || {})['aiParser'] || {}) as { [key: string]: any };
  }

  /** GME property -> the document's raw text, discarded because it matched no master-data row. */
  get discardedMetadata(): { [property: string]: string } {
    return this.aiParserOrderExtra['discardedMetadata'] || {};
  }

  /** GME properties whose value resolved but differs from the customer's registered one. */
  get deviatingMetadata(): string[] {
    const properties = this.aiParserOrderExtra['deviatingMetadata'];
    return Array.isArray(properties) ? properties : [];
  }

  /** The delivery address matches none of the customer's registered locations; server-recorded. */
  get unregisteredAddress(): boolean {
    return this.aiParserOrderExtra['unregisteredAddress'] === true;
  }

  /** What the document itself named, when the configuration's customer replaced it; empty otherwise. */
  get documentDeliveryCustomerId(): string {
    return this.aiParserOrderExtra['documentDeliveryCustomerId'] || '';
  }

  get documentBillingCustomerId(): string {
    return this.aiParserOrderExtra['documentBillingCustomerId'] || '';
  }

  get hasMetadataDeviations(): boolean {
    return this.deviatingMetadata.length > 0;
  }

  /** What the section holds that a reviewer must still rule on; it stays open while any of it stands. */
  get orderMetadataNeedsAttention(): boolean {
    return this.hasMetadataDeviations || this.unregisteredAddress
      || Object.keys(this.discardedMetadata).length > 0;
  }

  /** Marks the header tab, so a reviewer standing on the other tab sees it holds something. */
  get headerTabNeedsAttention(): boolean {
    return this.metadataHeaderFields.some(
      field => this.metadataDeviates(field.property) || !!this.metadataDiscarded(field.property)
    );
  }

  /** Same for the delivery address, which is also marked when it matches no registered location. */
  get deliveryAddressTabNeedsAttention(): boolean {
    return (
      this.unregisteredAddress ||
      this.metadataAddressFields.some(
        field => this.metadataDeviates(field.property) || !!this.metadataDiscarded(field.property)
      )
    );
  }

  /** Open when the reviewer opened it, or forced open while a decision is pending inside it. */
  get orderMetadataExpanded(): boolean {
    return this.orderMetadataOpen || this.orderMetadataNeedsAttention;
  }

  /** The confirmation is offered because values deviate, and is still unticked. Blocks submit. */
  get metadataConfirmRequired(): boolean {
    return this.hasMetadataDeviations && !this.confirmOrderMetadataDeviations;
  }

  /** The address is unregistered and the confirmation is still unticked. Blocks submit. */
  get unregisteredAddressConfirmRequired(): boolean {
    return this.unregisteredAddress && !this.confirmUnregisteredAddress;
  }

  /** The parser replaced the document's customer with the configuration's; server-recorded. */
  get customerOverridden(): boolean {
    return !!this.documentDeliveryCustomerId || !!this.documentBillingCustomerId;
  }

  /** The customer was overridden and the confirmation is still unticked. Blocks submit. */
  get customerOverrideConfirmRequired(): boolean {
    return this.customerOverridden && !this.confirmCustomerOverride;
  }

  metadataDeviates(property: string): boolean {
    return this.deviatingMetadata.indexOf(property) >= 0;
  }

  /** The raw text the document carried for a discarded property, or '' when nothing was discarded. */
  metadataDiscarded(property: string): string {
    return this.discardedMetadata[property] || '';
  }

  /** GME property -> what the order document stated, kept apart from the value that will post. */
  get documentMetadata(): { [property: string]: string } {
    return this.aiParserOrderExtra['documentMetadata'] || {};
  }

  /**
   * What the document said for a property, when that is not what the field currently holds —
   * the reviewer is looking at the customer's registered value and has no other way to see it.
   */
  metadataDocumentValue(property: string): string {
    const stated = this.documentMetadata[property];
    if (stated == null || stated === '') {
      return '';
    }
    const text = '' + stated;
    return text === (this.orderMetadata[property] || '') ? '' : text;
  }

  /** Take the document's value into the field, exactly as clicking a document price does on a row. */
  useDocumentMetadata(property: string): void {
    this.orderMetadata[property] = this.metadataDocumentValue(property);
    this.onOrderMetadataChanged();
  }

  /**
   * The delivery customer's registered delivery locations, offered as a starting point for the
   * address. Read-only: picking one fills the form, and Decision 12 stands — nothing here is ever
   * written back to customer master data.
   */
  private loadDeliveryLocations(): void {
    const customerId = this.editableDeliveryCustomerId;
    this.deliveryLocations = [];
    if (!customerId) {
      return;
    }
    this.customerService
      .getCustomer(customerId)
      .pipe(
        first(),
        switchMap(customer => (customer ? this.deliveryLocationsService.getDeliveryLocations(customer) : of([]))),
        catchError(() => of([]))
      )
      .subscribe(locations => {
        if (this.editableDeliveryCustomerId === customerId) {
          this.deliveryLocations = locations || [];
        }
      });
  }

  /**
   * The registered location the address fields describe right now, so the picker shows it on an
   * order that was matched or defaulted — including the customer's own address, which the order
   * carries without a ship-to code — and falls back to empty once the address is edited away.
   */
  get selectedDeliveryLocationCode(): string {
    const same = (a?: string, b?: string) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();
    const location = this.deliveryLocations.find(
      l =>
        same(l.address1, this.orderMetadata['deliveryAddress.address1']) &&
        same(l.zipCode, this.orderMetadata['deliveryAddress.zipCode']) &&
        same(l.city, this.orderMetadata['deliveryAddress.city'])
    );
    return location?.deliveryLocationCode || '';
  }

  onDeliveryLocationPicked(deliveryLocationCode: string): void {
    const location = this.deliveryLocations.find(l => l.deliveryLocationCode === deliveryLocationCode);
    if (!location) {
      return;
    }
    // The location carries AddressGME's own field names, so the copy is field-for-field.
    for (const field of AI_PARSER_METADATA_ADDRESS) {
      const addressField = field.property.split('.')[1] as keyof AiParserDeliveryLocation;
      this.orderMetadata[field.property] = location[addressField] || '';
    }
    this.onOrderMetadataChanged();
  }

  /**
   * The master-data table backing a property, or null when the installation configured none — or
   * configured one whose rows this installation does not carry. Either way the field falls back to
   * a plain text input, which is how a non-coded tenant gets free text with no per-ERP branching.
   */
  metadataCodedField(property: string): AiOrderParserCodedField | null {
    const coded = this.codedFields[property];
    if (!coded?.table) {
      return null;
    }
    return this.metadataService.getMetadataTableList(coded.table)?.length > 0 ? coded : null;
  }

  /** Provenance glyph: no config ever supplies order metadata, so it is the AI's value or a typed one. */
  metadataSource(property: string): CustomerFieldSource | null {
    return this.resolveSource(this.orderMetadata[property] || '', undefined, this.aiOrderMetadata[property] || '');
  }

  /**
   * The legend explains the glyphs the reviewer can actually see: an order parsed with no
   * configuration has no gear to explain, and an untouched one no pen. Mirrors exactly the four
   * places the template renders a source glyph.
   */
  get sourceLegend(): { icon: string; labelKey: string }[] {
    const shown = new Set(
      [
        this.deliveryDateSource,
        this.deliveryCustomerIdSource,
        this.billingCustomerIdSource,
        ...AI_PARSER_METADATA_FIELDS.map(field => this.metadataSource(field.property))
      ].filter(source => !!source)
    );
    return [
      { source: 'ai', icon: 'fa-wand-magic-sparkles', labelKey: 'AI_PARSER_SOURCE_AI' },
      { source: 'config', icon: 'fa-gear', labelKey: 'AI_PARSER_SOURCE_CONFIG' },
      { source: 'manual', icon: 'fa-pen', labelKey: 'AI_PARSER_SOURCE_MANUAL' }
    ]
      .filter(entry => shown.has(entry.source as CustomerFieldSource))
      .map(({ icon, labelKey }) => ({ icon, labelKey }));
  }

  onOrderMetadataChanged(property?: string): void {
    // lib-meta-select-input emits ngModelChange from writeValue on load, so only a real difference counts as an edit.
    const keys = new Set([...Object.keys(this.orderMetadata), ...Object.keys(this.metadataBaseline)]);
    if ([...keys].some(k => (this.orderMetadata[k] ?? '') + '' !== (this.metadataBaseline[k] ?? '') + '')) {
      this.hasPendingChanges = true;
      this.editVersion++;
    }
    // Another stock location means other stock figures, and so another stock verdict.
    if (property === 'stockId') {
      this.loadRowAvailabilities();
    }
  }

  /**
   * extra.gme.deliveryDate as yyyy-MM-dd for the date input. The backend stores it
   * as a Date, so it can arrive as an ISO timestamp; format the local date part
   * rather than slicing the string, which could shift the day across timezones.
   */
  private extractGmeDeliveryDate(order?: { extra?: { [key: string]: any } }): string {
    const gme = (order?.extra && order.extra['gme']) as Record<string, unknown> | undefined;
    const raw = gme?.['deliveryDate'];
    if (!raw) {
      return '';
    }
    if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      return raw;
    }
    const date = new Date(raw as string | number);
    if (isNaN(date.getTime())) {
      return '';
    }
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  /**
   * The row's own extra with the price decision applied. Accepting the document
   * price, or a manual override, writes OrderRows.Price, which the ERP order
   * pipeline keeps (it only fills Price in when absent); accepting the customer
   * price as-is leaves Price unset so the pipeline applies Gung's standard
   * customer price. Either way, customerUnitPrice itself is never touched — it
   * stays Gung's own record, only this order's line price changes. The
   * comparison data itself is persisted under `aiParser` for traceability. Used
   * both for the review order's rows and for the cart rows created in upload mode.
   */
  private buildRowExtra(row: EditableOrderRow): { [key: string]: any } {
    const extra: { [key: string]: any } = { ...(row.extra || {}) };

    const aiParser = { ...(extra['aiParser'] || {}) };
    if (row.documentUnitPrice != null) {
      aiParser['documentUnitPrice'] = row.documentUnitPrice;
    }
    if (row.customerUnitPrice != null) {
      aiParser['customerUnitPrice'] = row.customerUnitPrice;
    }
    if (row.manualUnitPrice != null) {
      aiParser['manualUnitPrice'] = row.manualUnitPrice;
    }
    if (row.ean) {
      aiParser['ean'] = row.ean;
    } else {
      delete aiParser['ean'];
    }
    aiParser['priceDeviates'] = !!row.priceDeviates;
    aiParser['priceMissing'] = !!row.priceMissing;
    if (row.priceChoice) {
      aiParser['priceChoice'] = row.priceChoice;
    } else {
      delete aiParser['priceChoice'];
    }
    extra['aiParser'] = aiParser;

    const orderRows = { ...(extra['OrderRows'] || {}) };
    if (row.priceChoice === 'document' && row.documentUnitPrice != null) {
      orderRows['Price'] = row.documentUnitPrice;
    } else if (row.priceChoice === 'manual' && row.manualUnitPrice != null) {
      orderRows['Price'] = row.manualUnitPrice;
    } else {
      delete orderRows['Price'];
    }
    if (Object.keys(orderRows).length > 0) {
      extra['OrderRows'] = orderRows;
    } else {
      delete extra['OrderRows'];
    }
    return extra;
  }

  /** Product id -> aggregated quantity over all rows, the price lookup's input shape. */
  private collectQtyByProduct(): { [productId: string]: number } {
    const qtyByProduct: { [productId: string]: number } = {};
    for (const row of this.editableRows) {
      // Skip rows whose product doesn't resolve: pricing a nonexistent article is pointless,
      // and a bad id in the batch can fail the whole price call for every row (e.g. BC's
      // price service throws on a null product). Backend-flagged not-found/missing rows are
      // exactly those ids, so leave them out of the request.
      const issues = row.validationIssues || [];
      if (
        issues.includes(AI_PARSER_ROW_FAULT.PRODUCT_NOT_FOUND) ||
        issues.includes(AI_PARSER_ROW_FAULT.MISSING_PRODUCT_ID)
      ) {
        continue;
      }
      if (row.productId && row.quantity > 0) {
        qtyByProduct[row.productId] = (qtyByProduct[row.productId] || 0) + row.quantity;
      }
    }
    return qtyByProduct;
  }

  /**
   * Fetch the customer's unit prices (Gung's standard price logic) for all rows
   * and flag the ones whose document price deviates. Runs in both modes; without
   * a delivery customer there is nothing to price against.
   */
  private loadRowPrices(): void {
    const customerId = this.editableDeliveryCustomerId;
    const qtyByProduct = this.collectQtyByProduct();
    if (!customerId || Object.keys(qtyByProduct).length === 0) {
      // No delivery customer or nothing to price: prices are definitively resolved as "none".
      this.pricesResolved = true;
      this.recomputeRowValidity();
      return;
    }
    this.isLoadingPrices = true;
    // Parser pricing path: the delivery customer is rarely the session's selected customer,
    // so the price service's selected-customer cache must not serve or store these.
    this.priceService.getCustomerPricesForParser(qtyByProduct, customerId).pipe(
      first(),
      timeout(10000),
      catchError(() => of([] as CustomerProductPrice[]))
    ).subscribe(prices => {
      this.isLoadingPrices = false;
      if (this.editableDeliveryCustomerId !== customerId) {
        // The delivery customer changed while the lookup was in flight; its reload owns the result.
        return;
      }
      if (prices.length > 0) {
        this.applyCustomerPrices(prices);
      } else {
        // Failed or empty lookup: the previous customer's comparison must not linger.
        this.clearCustomerPrices(true);
      }
      this.pricesResolved = true;
      this.recomputeRowValidity();
    });
  }

  /**
   * yyyy-MM-dd <-> NgbDateStruct bridge for the delivery-date picker. The string
   * form stays the source of truth (buildOrderExtra / extractGmeDeliveryDate read
   * it); the getter caches so it returns a stable object reference until the
   * underlying date changes, avoiding needless datepicker re-renders.
   */
  private deliveryDateStructCache: { key: string; value: NgbDateStruct | null } = { key: '\0', value: null };

  get deliveryDateStruct(): NgbDateStruct | null {
    const key = this.editableDeliveryDate || '';
    if (this.deliveryDateStructCache.key !== key) {
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
      this.deliveryDateStructCache = {
        key,
        value: match ? { year: +match[1], month: +match[2], day: +match[3] } : null
      };
    }
    return this.deliveryDateStructCache.value;
  }

  /** Datepicker selection/clear: write the string form, then refresh field-source glyphs. */
  onDeliveryDateChange(value: NgbDateStruct | string | null): void {
    // ngbDatepicker emits the raw text while it does not parse. Keep the last valid date (so
    // [ngModel] stays the same object and the input is not rewritten mid-typing) and flag it.
    if (typeof value === 'string') {
      this.deliveryDateInvalid = true;
      return;
    }
    this.deliveryDateInvalid = false;
    const pad = (n: number) => `${n}`.padStart(2, '0');
    this.editableDeliveryDate = value ? `${value.year}-${pad(value.month)}-${pad(value.day)}` : '';
    this.hasPendingChanges = true;
    this.editVersion++;
    this.recomputeCustomerSources();
    // The stock verdict accepts incoming stock only up to the delivery date.
    this.refreshRowFaults();
  }

  /** Called as the user edits the editable delivery customer id; re-pricing is debounced. */
  onEditableDeliveryCustomerIdChanged(): void {
    this.hasPendingChanges = true;
    this.editVersion++;
    this.recomputeCustomerSources();
    this.rowPriceReload$.next();
  }

  /** Called as the user edits a row's product ID; the stale product/name/link are cleared
   *  immediately, and a full revalidation (product lookup, availability, faults) is debounced. */
  onRowProductIdChanged(row: EditableOrderRow): void {
    this.hasPendingChanges = true;
    this.editVersion++;
    row.product = undefined;
    // The previous id's verdict says nothing about the one being typed.
    row.productLookupDone = false;
    row.ean = undefined;
    this.clearRowPrice(row);
    this.isValidated = false;
    this.rowProductIdChange$.next();
  }

  /**
   * Drop the price facts belonging to the article that was in this row a moment ago. Without this
   * the old article's customer price, deviation flag and the reviewer's price choice stay on screen
   * until the debounced re-pricing answers - and a stance chosen for one article must not carry
   * over to another one anyway.
   */
  private clearRowPrice(row: EditableOrderRow): void {
    row.customerUnitPrice = undefined;
    row.priceDeviates = false;
    row.priceMissing = false;
    row.priceChoice = null;
  }

  /**
   * Quantity edited. Refreshes the fault codes at once so the row state, the invalid styling and
   * the submit button stop reflecting the quantity as it was at the last validate.
   */
  onRowQuantityChanged(): void {
    this.hasPendingChanges = true;
    this.editVersion++;
    this.isValidated = false;
    this.refreshRowFaults();
  }

  /** Search hit picked in the product ID field's typeahead. preventDefault because ngbTypeahead
   *  would otherwise write the whole Product object into row.productId. Free text stays the
   *  fallback for anything the search doesn't surface. */
  onProductPicked(row: EditableOrderRow, event: NgbTypeaheadSelectItemEvent<Product>): void {
    event.preventDefault();
    this.hasPendingChanges = true;
    this.editVersion++;
    row.productId = event.item.id;
    row.product = event.item;
    row.productLookupDone = true;
    row.ean = undefined;
    this.clearRowPrice(row);
    this.isValidated = false;
    this.rowProductIdChange$.next();
    
    this.validate();
  }

  /** Dropdown row: both attributes, since the search matches either one. */
  productSearchFormatter(result: Product): string {
    return result.id + ' — ' + result.name;
  }

  /** What lands in the input once a hit is picked: the id, never the whole formatted row. */
  productIdFormatter = (result: Product | string): string =>
    typeof result === 'string' ? result : result?.id || '';

  /** ngbTypeahead source for the product-search picker. Reads the synced Product collection
   *  directly (never the ERP) via reviewService.searchLocalProducts - see that method's doc. */
  searchLocalProducts = (text$: Observable<string>): Observable<Product[]> => {
    return text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      switchMap(term =>
        term.trim().length < 2
          ? of([])
          : this.reviewService.searchLocalProducts(term.trim()).pipe(catchError(() => of([])))
      )
    );
  };

  /** ngbTypeahead source for the delivery/billing customer pickers: a reviewer retargeting an order
   *  has the company name off the document, not its customer number. Typing a bare id still works -
   *  the id is what the field holds either way. */
  searchCustomersByName = (text$: Observable<string>): Observable<Customer[]> => {
    return text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      switchMap(term =>
        term.trim().length < 2
          ? of([])
          : this.reviewService.searchCustomers(term.trim()).pipe(catchError(() => of([])))
      )
    );
  };

  /** Dropdown row: both attributes, since the search matches either one. */
  customerSearchFormatter(result: Customer): string {
    return result.id + ' — ' + result.name;
  }

  /** What lands in the input once a hit is picked: the id, never the whole formatted row. */
  customerIdFormatter = (result: Customer | string): string =>
    typeof result === 'string' ? result : result?.id || '';

  /** preventDefault because ngbTypeahead would otherwise write the whole Customer into the field. */
  onDeliveryCustomerPicked(event: NgbTypeaheadSelectItemEvent<Customer>): void {
    event.preventDefault();
    this.editableDeliveryCustomerId = event.item.id;
    this.onEditableDeliveryCustomerIdChanged();
  }

  onBillingCustomerPicked(event: NgbTypeaheadSelectItemEvent<Customer>): void {
    event.preventDefault();
    this.editableBillingCustomerId = event.item.id;
    this.hasPendingChanges = true;
    this.editVersion++;
    this.recomputeCustomerSources();
  }

  /** Re-price the rows against the edited delivery customer, or drop the comparison when cleared. */
  private reloadPricesForEditedCustomer(): void {
    this.refreshEditableDeliveryCustomerName();
    this.pricesResolved = false;
    if (!this.editableDeliveryCustomerId) {
      this.clearCustomerPrices();
      this.pricesResolved = true;
      this.recomputeRowValidity();
      return;
    }
    this.loadRowPrices();
  }

  /** Resolve the editable delivery customer's name, surfacing typos as "Unknown customer". */
  private refreshEditableDeliveryCustomerName(): void {
    const customerId = this.editableDeliveryCustomerId;
    this.editableDeliveryCustomerName = null;
    this.loadDeliveryLocations();
    if (!customerId) {
      return;
    }
    this.lookupCustomerName(customerId, name => {
      if (this.editableDeliveryCustomerId === customerId) {
        this.editableDeliveryCustomerName = name;
      }
    });
  }

  /** keepFixedChoices: keep 'document'/'manual' stances, which post a fixed price of their own. */
  private clearCustomerPrices(keepFixedChoices = false): void {
    for (const row of this.editableRows) {
      row.customerUnitPrice = undefined;
      row.priceDeviates = false;
      row.priceMissing = false;
      if (!keepFixedChoices || row.priceChoice === 'customer') {
        row.priceChoice = null;
      }
    }
  }

  /** Mark deviating rows per the spec rule and drop stale stances on rows that no longer deviate. */
  private applyCustomerPrices(prices: CustomerProductPrice[]): void {
    const byProduct = new Map(prices.map(p => [p.productId, p]));
    for (const row of this.editableRows) {
      // The customer price the current stance was taken against: this session's last lookup, or on a
      // fresh load the one persisted with the stance (buildRowExtra writes it, nothing reads it back).
      const persisted = (row.extra?.['aiParser'] as Record<string, unknown> | undefined)?.['customerUnitPrice'];
      const previousCustomerUnitPrice = row.customerUnitPrice !== undefined
        ? row.customerUnitPrice
        : typeof persisted === 'number' ? persisted : null;
      const price = row.productId ? byProduct.get(row.productId) : undefined;
      // The price endpoint answers every requested product; unknown products or
      // customers come back as 0 in the default currency. Treat those fallback
      // zeros as "no price resolved" so they never flag a deviation.
      const value = price?.customerNetPrice?.value;
      row.customerUnitPrice = value != null && value > 0 ? value : null;
      row.currencyCode = price?.customerNetPrice?.currencyCode || row.currencyCode;
      row.priceDeviates = row.documentUnitPrice != null
        && row.customerUnitPrice != null
        && pricesDeviate(row.documentUnitPrice, row.customerUnitPrice);
      row.priceMissing = row.documentUnitPrice == null && row.customerUnitPrice != null;
      // A manual override is an explicit stance the reviewer already took; a reprice (e.g. the
      // delivery customer changing) must not silently clear it just because the freshly
      // looked-up customerUnitPrice happens not to deviate from the document price anymore.
      if (!row.priceDeviates && !row.priceMissing && row.priceChoice !== 'manual') {
        // Document price matches the customer price: that is the price the row posts with anyway,
        // so mark it chosen instead of leaving both sides unmarked.
        row.priceChoice = row.customerUnitPrice != null ? 'customer' : null;
      } else if (row.priceChoice === 'customer' && row.customerUnitPrice !== previousCustomerUnitPrice) {
        // "Customer price" accepted a specific ERP price; a different one needs a fresh stance.
        // 'document' and 'manual' post a fixed price and stay valid whatever the ERP price does.
        row.priceChoice = null;
      }
    }
  }

  /**
   * Nothing about a row's prices can be judged before a delivery customer exists: no lookup has
   * run, so "valid" would mean "we did not check". Drives the green tick and the valid-count, both
   * of which otherwise report four healthy rows on a page whose only real problem is an empty
   * customer field.
   */
  get rowStateKnown(): boolean {
    return !!this.editableDeliveryCustomerId;
  }

  /** Rows the reviewer must take a stance on: the price deviates, or the document carried none. */
  needsPriceStance(row: EditableOrderRow): boolean {
    return !!row.priceDeviates || !!row.priceMissing;
  }

  get priceDeviationCount(): number {
    return this.editableRows.filter(r => this.needsPriceStance(r)).length;
  }

  /** Deviating rows where the reviewer has not yet chosen a price; blocks submit while > 0. */
  get unresolvedPriceDeviationCount(): number {
    return this.editableRows.filter(r => this.needsPriceStance(r) && !r.priceChoice).length;
  }

  /** Rows carrying a fault the invalid-row override must not wave through; blocks submit outright. */
  get unoverridableFaultRowCount(): number {
    return this.editableRows.filter(r =>
      r.faultCodes?.some(code => AI_PARSER_UNOVERRIDABLE_FAULTS.includes(code))
    ).length;
  }

  /** Row numbers (1-based, as printed in the table) carrying a blocking fault. */
  private get unoverridableFaultRowNumbers(): number[] {
    return this.editableRows
      .map((row, index) => ({ row, number: index + 1 }))
      .filter(({ row }) => row.faultCodes?.some(code => AI_PARSER_UNOVERRIDABLE_FAULTS.includes(code)))
      .map(({ number }) => number);
  }

  /** A fault no override reaches — submission is off the table until the row is fixed. */
  get hasBlockingFault(): boolean {
    return this.unoverridableFaultRowCount > 0;
  }

  /** Rows the reviewer may wave through, once nothing fatal is left. */
  get hasOverridableInvalidRows(): boolean {
    return this.allowInvalidRowOverride && this.invalidRowCount > 0 && !this.hasBlockingFault;
  }

  /** Always blocks submission, unconditionally — no override reaches it, same as price deviation.
   * Names the rows: "1 row has a problem" leaves the reviewer hunting for it in a long table.
   */
  get blockingFaultMessage(): string {
    return this.unoverridableFaultMessage;
  }

  /**
   * Always blocks submission, unconditionally — no override reaches it, same as price deviation.
   * Names the rows: "1 row has a problem" leaves the reviewer hunting for it in a long table.
   */
  private get unoverridableFaultMessage(): string {
    const numbers = this.unoverridableFaultRowNumbers;
    const message = this.instantCount('AI_PARSER_RESOLVE_BLOCKING_ROWS', numbers.length);
    return numbers.length > 0 ? `${message} (${numbers.map(n => '#' + n).join(', ')})` : message;
  }

  setPriceChoice(row: EditableOrderRow, choice: 'document' | 'customer'): void {
    row.priceChoice = choice;
    this.hasPendingChanges = true;
    this.editVersion++;
    // The stance decides which price posts, and therefore whether INVALID_PRICE applies.
    this.refreshRowFaults();
  }

  /**
   * Customer price typed in by hand. Written to manualUnitPrice, never to customerUnitPrice —
   * that field stays Gung's own record of the customer's real price. Marks the row 'manual' so
   * buildRowExtra writes the typed value as this order's Price override instead of leaving it
   * to the ERP's standard price lookup.
   */
  onCustomerPriceEdited(row: EditableOrderRow, value: number | null): void {
    row.manualUnitPrice = value;
    row.priceChoice = 'manual';
    this.hasPendingChanges = true;
    this.editVersion++;
    this.isValidated = false;
    this.refreshRowFaults();
  }

  /** The customer price to compare/display: the reviewer's typed override when set, otherwise
   *  Gung's computed price. Reading this never mutates customerUnitPrice. */
  effectiveCustomerPrice(row: EditableOrderRow): number | null {
    return row.priceChoice === 'manual' && row.manualUnitPrice != null ? row.manualUnitPrice : (row.customerUnitPrice ?? null);
  }

  /**
   * The unit price this row actually posts with: the document or manual override, matching
   * buildRowExtra's extra.OrderRows.Price rule exactly. Null when no override applies and the
   * ERP's own price logic decides the row.
   */
  private submittedUnitPrice(row: EditableOrderRow): number | null {
    if (row.priceChoice === 'document' && row.documentUnitPrice != null) {
      return row.documentUnitPrice;
    }
    if (row.priceChoice === 'manual' && row.manualUnitPrice != null) {
      return row.manualUnitPrice;
    }
    return null;
  }

  /** True once a manual override actually differs from Gung's original price — typing the same
   *  number back has nothing worth annotating with a hint and a redundant "+0.0%" badge. */
  manualPriceChanged(row: EditableOrderRow): boolean {
    if (row.priceChoice !== 'manual' || row.manualUnitPrice == null || row.customerUnitPrice == null) {
      return false;
    }
    return Math.abs(row.manualUnitPrice - row.customerUnitPrice) > 0.001;
  }

  /**
   * Bulk resolution: stamp the choice on every deviating row that has no stance
   * yet. Rows the reviewer already decided per-row are left untouched, so bulk +
   * per-row override compose. Persistence is unchanged — the choice is written to
   * row.extra.aiParser.* / extra.OrderRows.Price by buildRowExtra at save time.
   */
  applyBulkPriceChoice(choice: 'document' | 'customer'): void {
    for (const row of this.editableRows) {
      if (!this.needsPriceStance(row) || row.priceChoice) {
        continue;
      }
      // A row with no document price cannot take the document stance; leave it open rather than
      // stamping a choice that resolves to nothing at save time.
      if (choice === 'document' && row.documentUnitPrice == null) {
        continue;
      }
      this.setPriceChoice(row, choice);
    }
  }

  /** Clear the stance on all deviating rows, reopening the bulk resolution bar. */
  resetPriceChoices(): void {
    for (const row of this.editableRows) {
      if (this.needsPriceStance(row)) {
        row.priceChoice = null;
      }
    }
    this.hasPendingChanges = true;
    this.editVersion++;
    // The stance decides which price posts, and therefore whether INVALID_PRICE applies.
    this.refreshRowFaults();
  }

  /** Rows needing a stance, resolved to the document's price. */
  get documentPriceChoiceCount(): number {
    return this.editableRows.filter(r => this.needsPriceStance(r) && r.priceChoice === 'document').length;
  }

  /** Rows needing a stance, resolved to the customer's price (picked as-is or typed manually). */
  get customerPriceChoiceCount(): number {
    return this.editableRows.filter(r => this.needsPriceStance(r) && (r.priceChoice === 'customer' || r.priceChoice === 'manual')).length;
  }

  /**
   * Signed percentage difference of a selected price (document price, or the reviewer's manual
   * override) relative to Gung's real (original, never-edited) customer price ("+4.2%" / "−3.1%",
   * real minus sign), one decimal. Takes the two prices explicitly rather than deriving them from
   * the row, so the same comparison drives both the DOC.PRICE cell's discount badge (selected =
   * documentUnitPrice) and the CUSTOMER PRICE cell's one (selected = manualUnitPrice). Null when
   * either price is missing.
   */
  priceDeltaPercent(originalPrice: number | null | undefined, selectedPrice: number | null | undefined): string | null {
    if (originalPrice == null || selectedPrice == null || originalPrice === 0) {
      return null;
    }
    const delta = ((selectedPrice - originalPrice) / originalPrice) * 100;
    const sign = delta < 0 ? '−' : '+';
    // Whole percent: the badge rides on the price's own line now, and a tenth of a percent
    // was never what the reviewer was deciding on.
    const magnitude = Math.abs(delta);
    return `${sign}${magnitude < 10 ? magnitude.toFixed(1) : Math.round(magnitude)}%`;
  }

  /**
   * True when the selected price is below the original, false when it's above. Null when there is
   * no comparison to make, so neither colour is applied. Matches priceDeltaPercent's sign.
   */
  priceDeltaIsBelow(originalPrice: number | null | undefined, selectedPrice: number | null | undefined): boolean | null {
    if (originalPrice == null || selectedPrice == null || originalPrice === 0) {
      return null;
    }
    return selectedPrice < originalPrice;
  }

  /** Gung's own price for the row, for the manual-override badge's tooltip. */
  originalCustomerPriceHint(row: EditableOrderRow): string {
    const label = this.translateService.instant('AI_PARSER_ORIGINAL_CUSTOMER_PRICE');
    const price = (row.customerUnitPrice ?? 0).toFixed(2);
    return `${label}: ${price} ${row.currencyCode || ''}`.trim();
  }

  /** Scroll target of the validation summary's "N price deviations to resolve" anchor. */
  scrollToFirstUnresolvedRow(): void {
    const index = this.editableRows.findIndex(r => this.needsPriceStance(r) && !r.priceChoice);
    if (index < 0) {
      return;
    }
    document.getElementById('ai-parser-row-' + index)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * Entry point of the submit-to-ERP flow: gather the data for the confirmation
   * summary (customer master data for terms/delivery, customer prices for the
   * total), then ask for confirmation before anything is sent.
   */
  confirmSubmitToErp(): void {
    if (!this.reviewItemId || this.isPreparingSubmit || this.isSubmittingToErp || this.isValidating) {
      return;
    }
    if (this.unresolvedPriceDeviationCount > 0) {
      this.errorMessage = this.unresolvedPriceDeviationsMessage;
      return;
    }
    if (this.unoverridableFaultRowCount > 0) {
      this.errorMessage = this.unoverridableFaultMessage;
      return;
    }
    if (this.metadataConfirmRequired) {
      this.errorMessage = this.translateService.instant('AI_PARSER_METADATA_CONFIRM_REQUIRED');
      return;
    }
    if (this.unregisteredAddressConfirmRequired) {
      this.errorMessage = this.translateService.instant('AI_PARSER_ADDRESS_UNREGISTERED_REQUIRED');
      return;
    }
    if (this.customerOverrideConfirmRequired) {
      this.errorMessage = this.translateService.instant('AI_PARSER_CUSTOMER_OVERRIDE_REQUIRED');
      return;
    }
    this.errorMessage = '';
    const deliveryId = this.editableDeliveryCustomerId;
    const qtyByProduct = this.collectQtyByProduct();

    this.isPreparingSubmit = true;
    // The summary is best-effort: a missing customer or unresolvable prices must
    // never block submission, so both lookups fall back to empty values.
    forkJoin({
      customer: deliveryId
        ? this.customerService.getCustomer(deliveryId).pipe(first(), timeout(5000), catchError(() => of(null)))
        : of(null),
      prices: deliveryId && Object.keys(qtyByProduct).length > 0
        ? this.priceService.getCustomerPricesForParser(qtyByProduct, deliveryId).pipe(
            first(),
            timeout(5000),
            catchError(() => of([] as CustomerProductPrice[]))
          )
        : of([] as CustomerProductPrice[])
    }).subscribe(({ customer, prices }) => {
      this.isPreparingSubmit = false;
      if (this.editableDeliveryCustomerId !== deliveryId) {
        // Retargeted mid-preparation: this summary is for the old customer. Submit again.
        return;
      }
      // Re-check deviations against the prices just fetched — quantities or the
      // customer may have changed since the rows were last priced. A failed
      // lookup (empty list) drops the old comparison, as loadRowPrices does.
      if (prices.length > 0) {
        this.applyCustomerPrices(prices);
        if (this.unresolvedPriceDeviationCount > 0) {
          this.errorMessage = this.unresolvedPriceDeviationsMessage;
          return;
        }
      } else {
        this.clearCustomerPrices(true);
        this.refreshRowFaults();
        // Rows left without any price are now NO_PRICE; the submit gate decides again.
        if (!this.canSubmitToErp) {
          return;
        }
      }
      this.openSubmitConfirmModal(this.buildSubmitSummary(customer, prices, qtyByProduct));
    });
  }

  private get unresolvedPriceDeviationsMessage(): string {
    return this.instantCount('AI_PARSER_RESOLVE_TO_CONTINUE', this.unresolvedPriceDeviationCount);
  }

  /** instant() following the manifest's plural convention: `KEY_ONE` when count is 1, else `KEY` with {{count}}. */
  private instantCount(key: string, count: number): string {
    return this.translateService.instant(count === 1 ? `${key}_ONE` : key, { count });
  }

  private buildSubmitSummary(
    customer: Customer | null,
    prices: CustomerProductPrice[],
    qtyByProduct: { [productId: string]: number }
  ): AiOrderParserSubmitSummary {
    // Fortnox-style customer master data (ICE); other ERPs simply leave these fields blank.
    const erpCustomer = (customer?.extra && customer.extra['Customer']) || {};

    // Receipt rows: each row priced with the CHOSEN unit price — the document's price when the
    // reviewer accepted it, the reviewer's manual override when they typed one, otherwise the
    // customer's price from the fresh lookup (falling back to the table's cached comparison price
    // when the lookup failed). Rows without any resolvable price get a null price.
    const priceByProduct = new Map(prices.map(p => [p.productId, p]));
    const overriddenUnitPrice = (row: EditableOrderRow): number | null =>
      row.priceChoice === 'document' && row.documentUnitPrice != null
        ? row.documentUnitPrice
        : null;
    const manualUnitPrice = (row: EditableOrderRow): number | null =>
      row.priceChoice === 'manual' && row.manualUnitPrice != null ? row.manualUnitPrice : null;
    const receiptRows: AiOrderParserReceiptRow[] = this.editableRows.map((row, index) => {
      const price = priceByProduct.get(row.productId);
      const customerPrice = price?.customerNetPrice ? price.customerNetPrice.value : (row.customerUnitPrice ?? null);
      const unitPrice = overriddenUnitPrice(row) ?? manualUnitPrice(row) ?? customerPrice;
      return {
        rowNumber: index + 1,
        productId: row.productId,
        productName: row.product?.name || '',
        quantity: row.quantity || 0,
        unitPrice,
        lineTotal: unitPrice != null ? unitPrice * (row.quantity || 0) : null,
        currencyCode: price?.customerNetPrice?.currencyCode || row.currencyCode || '',
        usedDocumentPrice: overriddenUnitPrice(row) != null,
        customerPrice
      };
    });

    // Only show a total when every row resolved to a price — a partial sum is misleading.
    let totalValue: number | null = null;
    let currencyCode = '';
    if (receiptRows.length > 0 && receiptRows.every(row => row.lineTotal != null)) {
      totalValue = receiptRows.reduce((sum, row) => sum + (row.lineTotal || 0), 0);
      currencyCode = receiptRows.find(row => row.currencyCode)?.currencyCode || '';
    }

    const priceDeviations: AiOrderParserPriceDeviation[] = this.editableRows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => row.priceDeviates && row.priceChoice)
      .map(({ row, index }) => ({
        rowNumber: index + 1,
        productId: row.productId,
        documentPrice: row.documentUnitPrice!,
        customerPrice: row.customerUnitPrice!,
        chosen: row.priceChoice!,
        currencyCode: row.currencyCode || ''
      }));

    const metadata = (property: string): string => (this.orderMetadata[property] || '').trim();

    // Exactly what buildOrderExtra writes, read from the same form state, so the preview and the
    // submitted payload cannot drift. An empty value means the ERP applies its own default.
    const orderFields: AiOrderParserSubmitField[] = [
      { labelKey: 'AI_PARSER_CUSTOMER_ORDER_NO', value: this.customerOrderNumber },
      { labelKey: 'AI_PARSER_METADATA_REFERENCE_NO', value: metadata('customerOrderId') },
      ...(this.externalReferenceEnabled
        ? [{ labelKey: 'AI_PARSER_METADATA_YOUR_REFERENCE', value: metadata('externalReference') }]
        : []),
      {
        labelKey: 'AI_PARSER_DELIVERY_DATE',
        value: this.editableDeliveryDate,
        fallbackKey: 'AI_PARSER_SUBMIT_FIELD_ERP_DEFAULT'
      },
      {
        labelKey: 'AI_PARSER_METADATA_PAYMENT_TERMS',
        value: metadata('termsOfPayment'),
        fallbackKey: 'AI_PARSER_SUBMIT_FIELD_ERP_DEFAULT'
      },
      {
        labelKey: 'AI_PARSER_METADATA_SHIPPING_AGENT',
        value: metadata('shippingAgent'),
        fallbackKey: 'AI_PARSER_SUBMIT_FIELD_ERP_DEFAULT'
      },
      {
        labelKey: 'AI_PARSER_METADATA_DELIVERY_METHOD',
        value: metadata('deliveryMethod'),
        fallbackKey: 'AI_PARSER_SUBMIT_FIELD_ERP_DEFAULT'
      },
      {
        labelKey: 'AI_PARSER_METADATA_SALESPERSON',
        value: metadata('salesperson'),
        fallbackKey: 'AI_PARSER_SUBMIT_FIELD_ERP_DEFAULT'
      },
      { labelKey: 'AI_PARSER_METADATA_MESSAGE', value: metadata('externalText') }
    ];

    // The form's address is what ships; only an untouched form leaves the customer's master data to it.
    const formDeliveryAddress = [
      metadata('deliveryAddress.name'),
      metadata('deliveryAddress.address1'),
      metadata('deliveryAddress.address2'),
      [metadata('deliveryAddress.zipCode'), metadata('deliveryAddress.city')].filter(part => !!part).join(' '),
      metadata('deliveryAddress.countryCode')
    ].filter(line => line.length > 0);
    const deliveryAddressFromCustomer = formDeliveryAddress.length === 0;
    const deliveryAddress = deliveryAddressFromCustomer
      ? [
          erpCustomer['DeliveryName'],
          erpCustomer['DeliveryAddress1'],
          erpCustomer['DeliveryAddress2'],
          [erpCustomer['DeliveryZipCode'], erpCustomer['DeliveryCity']].filter(part => !!part).join(' '),
          erpCustomer['DeliveryCountry']
        ].filter(line => !!line && ('' + line).trim().length > 0)
      : formDeliveryAddress;

    return {
      deliveryCustomerId: this.editableDeliveryCustomerId,
      deliveryCustomerName: customer?.name || '',
      billingCustomerId: this.editableBillingCustomerId,
      customerOrderNumber: this.customerOrderNumber,
      senderEmail: this.senderEmailEnabled ? this.senderEmail.trim() : '',
      deliveryDate: this.editableDeliveryDate,
      rowCount: this.editableRows.length,
      totalQuantity: this.editableRows.reduce((sum, row) => sum + (row.quantity || 0), 0),
      invalidRowCount: this.editableRows.filter(row => row.isValid === false).length,
      totalValue,
      currencyCode,
      orderFields,
      deliveryAddress,
      deliveryAddressFromCustomer,
      priceDeviations,
      rows: receiptRows
    };
  }

  private openSubmitConfirmModal(summary: AiOrderParserSubmitSummary): void {
    const modalRef = this.modalService.open(PdfAiParserConfirmSubmitModalComponent, { centered: true, size: 'lg' });
    modalRef.componentInstance.summary = summary;
    modalRef.result.then(
      () => this.submitToErp(),
      () => { /* dismissed */ }
    );
  }

  /** Persist the reviewer's edits, then post the order to the ERP via the standard order pipeline. */
  private submitToErp(): void {
    if (!this.reviewItemId) {
      return;
    }
    const id = this.reviewItemId;
    this.isSubmittingToErp = true;
    this.errorMessage = '';
    this.authService.getCurrentUser().pipe(
      first(),
      switchMap(user => this.reviewService.updateOrder(id, this.buildReviewOrder(user))),
      switchMap(() =>
        this.reviewService.submitToErp(
          id,
          this.invalidRowsOverridden,
          this.hasMetadataDeviations && this.confirmOrderMetadataDeviations,
          this.confirmUnregisteredAddress,
          this.confirmCustomerOverride
        )
      )
    ).subscribe({
      next: item => {
        this.isSubmittingToErp = false;
        // updateOrder saved the edits before the submit, so leaving afterwards loses nothing.
        this.hasPendingChanges = false;
        this.metadataBaseline = { ...this.orderMetadata };
        // Stay on the page so the reviewer sees the ERP order number that was just created.
        this.reviewItem = item;
        this.justSubmittedToErp = true;
        this.notifySubmitSuccess(item);
      },
      error: () => {
        this.errorMessage = this.translateService.instant('AI_PARSER_ERROR_SUBMIT');
        this.isSubmittingToErp = false;
      }
    });
  }

  /** Success toast with the created ERP order number(s); the message links to the order page. */
  private notifySubmitSuccess(item: AiOrderParserItem): void {
    const erpIds = item.erpOrderIds || [];
    const title = this.translateService.instant('AI_PARSER_TOAST_SUBMITTED_TITLE');
    if (erpIds.length === 0) {
      this.gungNotificationService.notify(
        title,
        this.translateService.instant('AI_PARSER_TOAST_SUBMITTED_PENDING'),
        'success',
        undefined,
        10000
      );
      return;
    }
    this.gungNotificationService.notify(
      title,
      this.translateService.instant('AI_PARSER_TOAST_SUBMITTED_BODY', { ids: erpIds.join(', ') }),
      'success',
      { routerLink: ['/orders', erpIds[0]] },
      10000
    );
  }

  /** Approve/reject are only offered while the item can still change state. */
  get reviewFinalized(): boolean {
    const status = this.reviewItem?.status;
    return status === 'DONE' || status === 'REJECTED';
  }

  /** Finalized review items render read-only: row inputs locked, remove/price-choice chrome hidden. */
  get isReadOnly(): boolean {
    return this.reviewFinalized;
  }

  /**
   * Which terminal outcome note to show above the order content. ERROR only
   * counts when no fresh parse failure is on screen (that one owns the message), and not while a
   * re-read is running: the item keeps its previous error until the background parse finishes.
   */
  get reviewOutcome(): 'DONE' | 'REJECTED' | 'ERROR' | null {
    if (this.isParsing) {
      return null;
    }
    const status = this.reviewItem?.status;
    if (status === 'DONE' || status === 'REJECTED') {
      return status;
    }
    if (status === 'ERROR' && this.parseErrorMessage === null) {
      return 'ERROR';
    }
    return null;
  }

  /**
   * Trust-counter banner: why this order is in manual review, and how far its customers are
   * from the automatic threshold. Computed server-side, so these only read the item.
   */
  get trustStatus(): AiOrderParserTrustStatus | undefined {
    return this.reviewItem?.trustStatus;
  }

  get trustReason(): 'TRUST_RAMP' | 'PERIODIC_CHECK' | null {
    return this.trustStatus?.reason ?? null;
  }

  /** Clean parses still needed before the ramp ends; eligibility is `c > X`, so the target is X + 1. */
  get trustRemaining(): number {
    const status = this.trustStatus;
    if (!status) {
      return 0;
    }
    const counts = [status.delivery, status.billing]
      .filter(counter => !!counter)
      .map(counter => counter!.cleanSubmissions);
    const lowest = counts.length ? Math.min(...counts) : 0;
    return Math.max(0, status.threshold + 1 - lowest);
  }

  /**
   * Translate with a fallback: ngx-translate echoes the key when the remote catalogue has no
   * entry, which would print AI_PARSER_TRUST_* on screen. These two sentences are new, so they
   * read from the fallback until the keys are added to the translation service.
   */
  private translateOr(key: string, fallback: string, params?: Record<string, unknown>): string {
    const value = this.translateService.instant(key, params);
    return value === key ? fallback : value;
  }

  /**
   * What clearing the ramp changes — translation only, no fallback. What "trusted" buys a
   * customer depends on their settings and on the gung-core revision they run (in this
   * workspace's checkout the counter is advisory: AiOrderParserTrustService computes
   * manualReviewRequired, nothing consumes it to skip review, and submitToErp is only ever
   * reached from the controller a reviewer clicks). A sentence hardcoded here would state one
   * deployment's behaviour to all of them, so it stays empty until a tenant supplies the text.
   */
  get trustRampAfterExplainer(): string {
    const threshold = this.trustStatus?.threshold ?? 0;
    return this.translateOr(
      'AI_PARSER_TRUST_RAMP_AFTER',
      // Only the recurring check, which the rule itself guarantees (manual while
      // `cleanSubmissions % threshold == 0`) and which the PERIODIC_CHECK reason on this very
      // payload already encodes. What "trusted" otherwise buys a customer is deployment
      // -specific, so it stays out of this sentence.
      `Even once trusted, <strong>one order in every ${threshold}</strong> returns here for a spot check.`,
      { count: threshold }
    );
  }

  /** Clean parses still to go, as a sentence — the counter pill says where you are, this says how far. */
  get trustRemainingText(): string {
    return this.instantCount('AI_PARSER_TRUST_REMAINING', this.trustRemaining);
  }

  /** Delivery and billing counters side by side; the head's pill only shows the lower of the two. */
  get trustCounterRows(): { labelKey: string; customerId: string; cleanSubmissions: number }[] {
    const rows: { labelKey: string; customerId: string; cleanSubmissions: number }[] = [];
    const delivery = this.trustStatus?.delivery;
    const billing = this.trustStatus?.billing;
    if (delivery) {
      rows.push({
        labelKey: 'AI_PARSER_DELIVERY_CUSTOMER',
        customerId: delivery.customerId,
        cleanSubmissions: delivery.cleanSubmissions
      });
    }
    if (billing) {
      rows.push({
        labelKey: 'AI_PARSER_BILLING_CUSTOMER',
        customerId: billing.customerId,
        cleanSubmissions: billing.cleanSubmissions
      });
    }
    return rows;
  }

  /** Resolved names for the trust card's account pills, by customer id. */
  trustCustomerNames: Record<string, string> = {};

  /** Fills trustCustomerNames for whichever customers the trust status names. */
  private loadTrustCustomerNames(): void {
    const ids = [this.trustStatus?.delivery?.customerId, this.trustStatus?.billing?.customerId].filter(
      (id): id is string => !!id
    );
    for (const id of new Set(ids)) {
      if (this.trustCustomerNames[id] !== undefined) {
        continue;
      }
      this.lookupCustomerName(id, name => (this.trustCustomerNames[id] = name));
    }
  }

  /** True when one account is on the ramp, not two — then the card shows a single pill. */
  get trustSingleCustomer(): boolean {
    const delivery = this.trustStatus?.delivery?.customerId;
    const billing = this.trustStatus?.billing?.customerId;
    return !delivery || !billing || delivery === billing;
  }

  /**
   * How many clean parses in a row the ramp actually needs. The backend gate is
   * `cleanSubmissions <= threshold`, so a threshold of 7 still reviews the 7th order and lets
   * the 8th through — the bar counts to threshold + 1 rather than to the threshold itself.
   */

  /** The billing counter, only when it is a different customer than the delivery one. */
  get billingTrustCounter(): AiOrderParserTrustCounter | null {
    const billing = this.trustStatus?.billing;
    if (!billing || billing.customerId === this.trustStatus?.delivery?.customerId) {
      return null;
    }
    return billing;
  }

  /** Clean parses banked so far — the weaker of the two customers on the order. */
  get trustProgressCurrent(): number {
    const counters = [this.trustStatus?.delivery, this.trustStatus?.billing].filter(counter => !!counter);
    return counters.length ? Math.min(...counters.map(counter => counter!.cleanSubmissions)) : 0;
  }

  /** Eligibility is `clean > threshold`, so the bar's target is threshold + 1. */
  get trustProgressTarget(): number {
    return (this.trustStatus?.threshold ?? 0) + 1;
  }

  get trustProgressPercent(): number {
    const target = this.trustProgressTarget;
    return target > 0 ? Math.round((this.trustProgressCurrent / target) * 100) : 0;
  }

  /**
   * Sender-gate reason, one line, only for the manual-review decisions — AUTO_PARSED needs no
   * explanation since nothing went wrong.
   */
  get senderAuthReasonKey(): string | undefined {
    switch (this.reviewItem?.autoParseDecision) {
      case 'MANUAL_UNAUTHENTICATED':
        return 'AI_PARSER_SENDER_GATE_REASON_UNAUTHENTICATED';
      case 'MANUAL_UNTRUSTED_FORWARDER':
        return 'AI_PARSER_SENDER_GATE_REASON_UNTRUSTED_FORWARDER';
      case 'MANUAL_SPAM_SUSPECTED':
        return 'AI_PARSER_SENDER_GATE_REASON_SPAM_SUSPECTED';
      default:
        return undefined;
    }
  }

  /** When the item reached its DONE outcome: posted-to-ERP time, falling back to review time. */
  get reviewOutcomeDate(): string | null {
    return this.reviewItem?.postedToErpAt || this.reviewItem?.reviewedAt || null;
  }

  /**
   * Parse button label: "Parse with this configuration" for never-parsed inbox
   * items (AWAITING_CONFIGURATION empty state), "Re-parse" once a result exists,
   * plain "Parse order" otherwise.
   */
  get parseButtonLabelKey(): string {
    if (this.isLoading) {
      return 'AI_PARSER_STATUS_PARSING';
    }
    if (this.response) {
      return 'AI_PARSER_REPARSE';
    }
    if (this.reviewItem?.status === 'AWAITING_CONFIGURATION') {
      // Parsing without a config is allowed, so only promise "with config" when there is one.
      return this.selectedConfigName ? 'AI_PARSER_PARSE_WITH_CONFIG' : 'AI_PARSER_PARSE_ORDER';
    }
    return 'AI_PARSER_PARSE_ORDER';
  }

  /** Download the original attachment ("Open original" icon-button). */
  downloadOriginal(): void {
    if (!this.reviewItemId) {
      return;
    }
    this.reviewService.fetchAttachmentBlob(this.reviewItemId).pipe(first()).subscribe(blob => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = this.reviewItem?.originalFilename || 'order';
      link.click();
      URL.revokeObjectURL(url);
    });
  }

  /** The backend only allows posting from AWAITING_REVIEW; when blocking is enabled,
   *  also require every row to be valid (mirrors the server-side gate). */
  get canSubmitToErp(): boolean {
    if (this.reviewItem?.status !== 'AWAITING_REVIEW') {
      return false;
    }
    // A validate/save is still writing the order; submitting now would race its PUT.
    if (this.isValidating) {
      return false;
    }
    // enforceCustomerIdentity refuses without one, so there is nothing to gain from letting the
    // click through - and the rows cannot be priced until it is filled in either.
    if (!this.editableDeliveryCustomerId) {
      return false;
    }
    if (this.deliveryDateInvalid) {
      return false;
    }
    // confirmSubmitToErp() refuses on these too - without this the button invites a click that
    // can only end in an error message.
    if (this.unresolvedPriceDeviationCount > 0) {
      return false;
    }
    if (this.unoverridableFaultRowCount > 0) {
      return false;
    }
    if (this.invalidRowCount > 0 && !this.invalidRowsOverridden) {
      return false;
    }
    if (this.metadataConfirmRequired) {
      return false;
    }
    if (this.unregisteredAddressConfirmRequired) {
      return false;
    }
    if (this.customerOverrideConfirmRequired) {
      return false;
    }
    return true;
  }

  /** True once the reviewer has ticked the override on an installation that permits it. */
  get invalidRowsOverridden(): boolean {
    return this.allowInvalidRowOverride && this.overrideInvalidRows;
  }

  openRejectModal(): void {
    this.rejectionReason = '';
    this.modalService.open(this.rejectModalTpl, { centered: true }).result.then(
      () => this.rejectReview(),
      () => { /* dismissed */ }
    );
  }

  rejectReview(): void {
    if (!this.reviewItemId) {
      return;
    }
    this.isRejecting = true;
    this.reviewService.reject(this.reviewItemId, this.rejectionReason).subscribe({
      next: () => {
        this.isRejecting = false;
        // Rejected: the edits are intentionally discarded, no need to ask on the way out.
        this.hasPendingChanges = false;
        this.router.navigate(['/order-parser']);
      },
      error: () => {
        this.errorMessage = this.translateService.instant('AI_PARSER_ERROR_REJECT');
        this.isRejecting = false;
      }
    });
  }

  openResolveOutcomeModal(): void {
    this.resolveErpOrderId = '';
    this.modalService.open(this.resolveOutcomeModalTpl, { centered: true }).result.then(
      (choice: 'posted' | 'not-posted') => this.resolveOutcome(choice === 'posted'),
      () => { /* dismissed */ }
    );
  }

  /** Guards the "confirm posted" button: a value of only commas/whitespace parses to no ids. */
  get resolveErpOrderIdValid(): boolean {
    return this.resolveErpOrderId.split(',').some(v => v.trim().length > 0);
  }

  private resolveOutcome(posted: boolean): void {
    if (!this.reviewItemId) {
      return;
    }
    this.isResolvingOutcome = true;
    const erpOrderIds = posted && this.resolveErpOrderId
      ? this.resolveErpOrderId.split(',').map(v => v.trim()).filter(v => v.length > 0)
      : undefined;
    this.reviewService.resolveOutcome(this.reviewItemId, posted, erpOrderIds).subscribe({
      next: item => {
        this.isResolvingOutcome = false;
        this.reviewItem = item;
      },
      error: () => {
        this.errorMessage = this.translateService.instant('AI_PARSER_ERROR_RESOLVE_OUTCOME');
        this.isResolvingOutcome = false;
      }
    });
  }

  backToReviewList(): void {
    this.router.navigate(['/order-parser']);
  }

  /** The customer's own order number from the document (not the ERP order id). */
  get customerOrderNumber(): string {
    const extra = this.reviewItem?.parsedOrder?.extra;
    return (extra && extra['customerOrderNumber']) || '';
  }

  /** Where this order originated: emailed in, or uploaded manually. */
  get orderSource(): 'EMAIL' | 'UPLOAD' {
    return this.reviewItem?.fromEmail ? 'EMAIL' : 'UPLOAD';
  }

  getRows(): ParsedOrderRow[] {
    return this.response?.order?.rows || [];
  }

  /** Blank row appended for manual entry — slots into the same typeahead/pricing/validation
   *  pipeline as parsed rows once the reviewer fills in a product id. */
  addRow(): void {
    this.hasPendingChanges = true;
    this.editVersion++;
    this.editableRows.push({ productId: '', quantity: 1 });
    this.isValidated = false;
    this.refreshRowFaults();
  }

  removeRow(index: number): void {
    this.hasPendingChanges = true;
    this.editVersion++;
    this.editableRows.splice(index, 1);
    // Recalculate counts if already validated
    if (this.isValidated) {
      this.updateValidationCounts();
    }
  }

  /**
   * The table's validate button: the user's edits are persisted and the backend's own
   * validation pipeline re-runs against the saved order, so warnings come back
   * authoritative from the same pipeline that produced the initial set.
   */
  validate(): void {
    this.validateAndSaveReview();
  }

  /** Label for the validate button. */
  get validateButtonLabel(): string {
    const key = this.isValidating ? 'AI_PARSER_SAVING' : 'AI_PARSER_VALIDATE_SAVE';
    return this.translateService.instant(key);
  }

  private validateAndSaveReview(): void {
    if (!this.reviewItemId || this.editableRows.length === 0 || this.deliveryDateInvalid) {
      return;
    }
    if (this.isValidating) {
      this.revalidateQueued = true;
      return;
    }
    const id = this.reviewItemId;
    const sentVersion = this.editVersion;
    this.isValidating = true;
    this.authService.getCurrentUser().pipe(
      first(),
      switchMap(user => this.reviewService.updateOrder(id, this.buildReviewOrder(user))),
      switchMap(() => this.reviewService.revalidate(id))
    ).subscribe({
      next: updated => {
        this.reviewItem = updated;
        // Edited since sending: the response describes rows that may have moved, so don't map
        // it back by index; the next validate refreshes them.
        const unchanged = this.editVersion === sentVersion;
        if (unchanged) {
          // The backend re-ran the validators; pull the refreshed per-row issue codes back
          // onto the rows (by position — revalidate never reorders) before recomputing.
          const updatedRows = updated.parsedOrder?.rows || [];
          let productIdsChanged = false;
          this.editableRows.forEach((row, i) => {
            const updatedExtra = this.readAiParserRowExtra(updatedRows[i]?.extra);
            row.validationIssues = updatedExtra.validationIssues || [];
            row.ean = updatedExtra.ean;
            // The backend resolves an EAN to the sku; take it so the lookup below finds the product.
            const resolvedId = updatedRows[i]?.productId;
            if (resolvedId && resolvedId !== row.productId) {
              row.productId = resolvedId;
              row.product = undefined;
              row.productLookupDone = false;
              productIdsChanged = true;
            }
          });
          if (productIdsChanged) {
            this.loadRowProducts();
          }
        }
        if (this.response) {
          this.response = { ...this.response, warnings: updated.warnings || [] };
        }
        this.recomputeRowValidity();
        this.loadRowPrices();
        if (unchanged) {
          this.hasPendingChanges = false;
          this.metadataBaseline = { ...this.orderMetadata };
        }
        this.isValidating = false;
        this.runQueuedValidate();
      },
      error: () => {
        this.errorMessage = this.translateService.instant('AI_PARSER_ERROR_SAVE_VALIDATE');
        this.isValidating = false;
        this.runQueuedValidate();
      }
    });
  }

  private runQueuedValidate(): void {
    if (this.revalidateQueued) {
      this.revalidateQueued = false;
      this.validateAndSaveReview();
    }
  }

  private updateValidationCounts(): void {
    this.validRowCount = this.editableRows.filter(r => r.isValid).length;
    this.invalidRowCount = this.editableRows.filter(r => r.isValid === false).length;
  }

  /**
   * Resolve product master data for the rows so the table's Description column
   * can show the product names. Upload-mode validation assigns row.product
   * itself; this fills the same field for rows arriving pre-validated from the
   * backend (review mode and fresh parses). Best-effort — rows without a match
   * keep their muted em-dash.
   */
  private loadRowProducts(): void {
    const ids = Array.from(new Set(this.editableRows.map(r => r.productId).filter(id => !!id)));
    if (ids.length === 0) {
      return;
    }
    // Reads the synced Product collection directly (reviewService.getProductsByIdsLocal), never
    // the ERP: getProductsByIdsUnfiltered goes through ProductService.getProduct(), which for
    // Fortnox calls the live API once per unique product on every page load. This is UI display
    // data - a customer's real PO can also reference a product outside whatever flow/assortment
    // the reviewer's session has selected, so this never filters by flow membership either.
    this.reviewService.getProductsByIdsLocal(ids).pipe(first()).subscribe({
      next: (products: Product[]) => {
        const byId = new Map(products.map(p => [p.id, p]));
        for (const row of this.editableRows) {
          // Rows edited since the request went out are not answered by it; their own lookup is.
          if (!row.productId || !ids.includes(row.productId)) {
            continue;
          }
          row.product = byId.get(row.productId);
          // A miss is a fact now, not just an empty name: PRODUCT_NOT_FOUND depends on it.
          row.productLookupDone = true;
        }
        this.refreshRowFaults();
      },
      error: () => {
        // Lookup unavailable: rows keep whatever they had, and no row is called not-found
        // on the strength of a failed request.
      }
    });
  }

  /**
   * The stock used while the order names none: the reviewer's first stock, else the config's
   * responsible user's, else - only with pickStockCoveringMostRows - the stockId table's location
   * whose stock covers the most rows (ties: table order). Where stockId is coded, a candidate that
   * is not a code of its table is skipped. The customer's location needs no step here: the parse
   * already starts extra.gme.stockId at it (customer-fields.stockId).
   */
  private resolveDefaultStockId(
    item: AiOrderParserItem,
    settings: AiOrderParserSettings,
    user: User
  ): Observable<string | undefined> {
    const coded = this.metadataCodedField('stockId');
    const codes = coded ? this.metadataService.getMetadataTableList(coded.table).map(row => '' + row[coded.codeField]) : [];
    const usable = (stockId?: string) => !!stockId && (!coded || codes.includes(stockId));
    const responsibleUsername = this.configs.find(c => c.name === item.assignedConfigName)?.responsibleUsername;
    // All users is the only lookup of another user there is, and admin-only: anyone else skips this step.
    const users$ = usable(user.managedMultistockIds?.[0]) || !responsibleUsername
      ? of([])
      : this.usersService.getAllUsers().pipe(first(), catchError(() => of([])));
    return users$.pipe(
      switchMap(users => {
        const responsible = users.find(u => u.username === responsibleUsername);
        const stockId = [user.managedMultistockIds?.[0], responsible?.managedMultistockIds?.[0]].find(usable);
        const ids = Array.from(new Set(this.editableRows.map(r => r.productId).filter(id => !!id)));
        if (stockId || !settings?.pickStockCoveringMostRows || codes.length === 0 || ids.length === 0) {
          return of(stockId);
        }
        // One availability call per location: fine for the handful of stocks a tenant has.
        return forkJoin(
          codes.map(code =>
            this.availabilityService.getAvailabilities(ids, code).pipe(
              first(),
              map(availabilities => {
                const byId = new Map(availabilities.map(a => [a.productId, a.currentAvailability]));
                return this.editableRows.filter(r => (byId.get(r.productId) ?? 0) >= (r.quantity ?? 0)).length;
              }),
              catchError(() => of(0))
            )
          )
        ).pipe(map(covered => codes[covered.indexOf(Math.max(...covered))]));
      })
    );
  }

  /**
   * Fetch current availability for the indicator under each quantity input, through the
   * same shared AvailabilityService the product list uses, for the resolved stockId.
   * No-op unless the stock is shown or checked.
   * Best-effort: on error the indicators simply stay hidden.
   */
  private loadRowAvailabilities(): void {
    if (!this.showStock && !this.checkStock) {
      return;
    }
    const ids = Array.from(new Set(this.editableRows.map(r => r.productId).filter(id => !!id)));
    if (ids.length === 0) {
      return;
    }
    const stockId = this.stockId;
    this.availabilityService.getAvailabilities(ids, stockId).pipe(first()).subscribe({
      next: (availabilities: Availability[]) => {
        if (this.stockId !== stockId) {
          // The stock location changed while in flight; its own reload owns the result.
          return;
        }
        const byId = new Map(availabilities.map(a => [a.productId, a]));
        for (const row of this.editableRows) {
          row.availability = row.productId ? byId.get(row.productId) : undefined;
        }
        this.refreshRowFaults();
      },
      error: () => {
        // Indicators stay hidden; the review flow is unaffected.
      }
    });
  }

  /** Indicator state for the row, recomputed live as the reviewer edits the quantity. */
  availabilityState(row: EditableOrderRow): AiParserAvailabilityState | null {
    return computeAvailabilityState(
      row.quantity,
      row.availability?.currentAvailability,
      row.availability?.maxFutureStock
    );
  }

  /** Tooltip for the availability indicator; mentions incoming stock in the orange state. */
  availabilityTitle(row: EditableOrderRow): string {
    const current = this.translateService.instant('AI_PARSER_AVAILABILITY_CURRENT', {
      current: row.availability?.currentAvailability
    });
    if (this.availabilityState(row) === 'incoming') {
      return (
        current +
        ' ' +
        this.translateService.instant('AI_PARSER_AVAILABILITY_INCOMING', {
          incoming: row.availability?.maxFutureStock
        })
      );
    }
    return current;
  }

  /**
   * Which price column a price fault belongs to: the document price when that is the negative
   * number the reviewer can see, the customer price in every other case (a missing price, or a
   * negative one the reviewer typed).
   */
  private priceFaultColumn(row: EditableOrderRow): 'docPrice' | 'custPrice' {
    return row.documentUnitPrice != null && row.documentUnitPrice < 0 ? 'docPrice' : 'custPrice';
  }

  /** Fault codes attached to one control of the row. */
  private fieldFaultCodes(row: EditableOrderRow, field: AiParserFaultFieldSlot): string[] {
    return (row.faultCodes || []).filter(code => {
      const target = AI_PARSER_ROW_FAULT_FIELDS[code] || 'product';
      return target === 'price' ? this.priceFaultColumn(row) === field : target === field;
    });
  }

  /**
   * How hard a fault is: 'error' for the ones "submit despite invalid rows" can never wave
   * through, 'warning' for the judgement calls it can. With the override switched off for the
   * customer nothing is a judgement call, so everything reads as an error.
   */
  private codeSeverity(code: string): AiParserFaultSeverity {
    if (!this.allowInvalidRowOverride) {
      return 'error';
    }
    return AI_PARSER_UNOVERRIDABLE_FAULTS.includes(code) ? 'error' : 'warning';
  }

  /** Severity of the faults sitting on one control, or null when that control is fine. */
  fieldSeverity(row: EditableOrderRow, field: AiParserFaultFieldSlot): AiParserFaultSeverity | null {
    const codes = this.fieldFaultCodes(row, field);
    if (codes.some(code => this.codeSeverity(code) === 'error')) {
      return 'error';
    }
    
    let isWarning = codes.length > 0;
    
    // Highlight the QTY field if stock is short/out
    if (field === 'quantity') {
      const availState = this.availabilityState(row);
      if (this.showStock && row.availability && availState && availState !== 'ok') {
        isWarning = true;
      }
    }
    
    return isWarning ? 'warning' : null;
  }

  /** Translated fault messages for one control, one per line (tooltips render them pre-line). */
  fieldFaultText(row: EditableOrderRow, field: AiParserFaultFieldSlot): string {
    const texts = this.fieldFaultCodes(row, field)
      .map(code => this.translateService.instant(AI_PARSER_ROW_FAULT_KEYS[code] || code));

    // Append stock warning to the QTY input's tooltip
    if (field === 'quantity') {
      const availState = this.availabilityState(row);
      if (this.showStock && row.availability && availState && availState !== 'ok') {
        texts.push(this.availabilityTitle(row));
      }
    }
    
    return texts.join('\n');
  }

  /** Every fault on the row, one per line — the marker glyph's tooltip. The stock line is already
   *  in validationErrors, put there by refreshRowFaults. */
  rowFaultText(row: EditableOrderRow): string {
    return (row.validationErrors || []).join('\n');
  }

  /** Worst severity anywhere in the row — what the marker glyph shows. */
  rowSeverity(row: EditableOrderRow): AiParserFaultSeverity | null {
    const codes = row.faultCodes || [];
    if (codes.some(code => this.codeSeverity(code) === 'error')) {
      return 'error';
    }
    
    let isWarning = codes.length > 0;
    
    // Show the yellow warning triangle on the row if stock is short/out
    const availState = this.availabilityState(row);
    if (this.showStock && row.availability && availState && availState !== 'ok') {
      isWarning = true;
    }
    
    return isWarning ? 'warning' : null;
  }

  /**
   * The one currency the rows share, so the price columns can carry it in their header instead of
   * repeating it inside every read-only value. Null when the rows disagree — then each value
   * prints its own code, since a column header cannot speak for all of them.
   */
  get rowsCurrencyCode(): string | null {
    const codes = new Set(this.editableRows.map(row => row.currencyCode).filter(code => !!code));
    return codes.size === 1 ? [...codes][0]! : null;
  }

  /**
   * What the stock figure says on hover: the availability wording, plus the row's product fault
   * when it has one — the faults used to be tooltips on the inputs themselves, where they covered
   * the product name the reviewer was trying to read while typing.
   */
  rowStockTooltip(row: EditableOrderRow): string {
    const fault = this.fieldFaultText(row, 'product');
    const availability = this.availabilityTitle(row);
    return fault ? `${availability}\n${fault}` : availability;
  }

  /**
   * Recompute each row's validity from the backend verdict (extra.aiParser.validationIssues)
   * plus the frontend quantity and customer-price gates. Backend facts and the price/qty gates
   * are merged into one fault list so the reviewer sees every problem at once. Price *deviation*
   * is not a fault here — a deviating row still has a customer price, so it stays valid; the
   * amber "pick one" state is the template's priceDeviates && !priceChoice gate. Safe to call
   * repeatedly (after parse/load, after prices settle, after the backend revalidate).
   */
  private recomputeRowValidity(): void {
    this.refreshRowFaults();
    this.isValidated = true;
    this.loadRowProducts();
    this.loadRowAvailabilities();
  }

  /**
   * Recompute the fault codes from the rows' current state. No network calls and no change to
   * isValidated, so it is safe to run on every edit — that flag means "the backend has seen this
   * order", which typing cannot make true.
   */
private refreshRowFaults(): void {
    for (const row of this.editableRows) {
      const qtyValidation = this.checkoutQtyValidation(row);
      const faults = computeRowFaults({
        validationIssues: row.validationIssues,
        productId: row.productId,
        productFound: row.productLookupDone ? !!row.product : undefined,
        quantity: row.quantity,
        documentUnitPrice: row.documentUnitPrice,
        customerUnitPrice: this.effectiveCustomerPrice(row),
        selectedUnitPrice: this.submittedUnitPrice(row),
        customerSelected: !!this.editableDeliveryCustomerId,
        pricesResolved: this.pricesResolved,
        unavailable: !!qtyValidation && !qtyValidation.valid
      });

      // Check stock availability
      const availState = this.availabilityState(row);
      const isStockShort = this.showStock && row.availability && availState && availState !== 'ok';

      // 1. Row is invalid if it has faults; a stock shortage counts only via PRODUCT_UNAVAILABLE (checkout's verdict)
      row.isValid = faults.length === 0;
      row.faultCodes = faults;
      
      row.validationErrors = faults.map(code =>
        code === AI_PARSER_ROW_FAULT.PRODUCT_UNAVAILABLE && qtyValidation?.description
          ? qtyValidation.description
          : this.translateService.instant(AI_PARSER_ROW_FAULT_KEYS[code] || code)
      );

      // 2. Append the stock warning to the generic error list so tooltips catch it
      if (isStockShort) {
        row.validationErrors.push(this.availabilityTitle(row));
      }
    }
    
    // 3. Trigger the tally count update
    this.updateValidationCounts();
  }

  /**
   * Web checkout's quantity validation (QuantityValidationStepComponent's verdict) against the
   * availability the stock column shows. Null when the stock is not checked (see checkStock) or no
   * availability has loaded. The row is a throwaway: the service writes the delivery date onto it.
   */
  private checkoutQtyValidation(row: EditableOrderRow): ValidationResult | null {
    if (!this.checkStock || !row.availability) {
      return null;
    }
    return this.checkoutQuantityValidationService.getQtyValidationResult({
      row: { id: row.productId, quantity: row.quantity, extra: { ...row.extra, gme: { deliveryDate: this.editableDeliveryDate } } },
      checkout: { rows: [], extra: {} },
      product: row.product,
      availability: row.availability,
      calendarValidDeliveryDates: null
    });
  }

  /** All pipeline warnings — every warning is order-level; row-level facts live on the rows. */
  get orderLevelWarnings(): AiOrderParserWarning[] {
    return this.response?.warnings || [];
  }

  /** Total units over all rows, for the action bar's rollup caption. */
  get totalUnits(): number {
    return this.editableRows.reduce((sum, row) => sum + (row.quantity || 0), 0);
  }

  /**
   * Order total for the review-mode rollup: sum of qty × chosen-or-customer unit price. Rows
   * without a usable price are left out and the total is flagged partial (rendered with a
   * leading ≥) rather than suppressed: one unpriced row used to blank the figure entirely,
   * which reads as "no total" instead of "one row still needs a price".
   */
  get reviewOrderTotal(): { value: number; currencyCode: string; partial: boolean } | null {
    if (this.editableRows.length === 0) {
      return null;
    }
    let value = 0;
    let currencyCode = '';
    let priced = 0;
    for (const row of this.editableRows) {
      const unitPrice = row.priceChoice === 'document' && row.documentUnitPrice != null
        ? row.documentUnitPrice
        : this.effectiveCustomerPrice(row);
      if (unitPrice == null) {
        continue;
      }
      priced++;
      value += unitPrice * (row.quantity || 0);
      currencyCode = row.currencyCode || currencyCode;
    }
    if (priced === 0) {
      return null;
    }
    return { value, currencyCode, partial: priced < this.editableRows.length };
  }

  /**
   * Why the action bar's primary action is currently blocked, surfaced as a
   * polite live-region caption next to the disabled button. Null when nothing
   * blocks (or while an action is already running).
   */
  get commitBlockedReason(): string | null {
    if (this.reviewFinalized || this.isSubmittingToErp || this.isPreparingSubmit) {
      return null;
    }
    // A blocking fault is spelled out in its own banner above the bar, so the only reason
    // left worth a caption is an unresolved price deviation.
    return this.unresolvedPriceDeviationCount > 0
      ? this.instantCount('AI_PARSER_RESOLVE_TO_CONTINUE', this.unresolvedPriceDeviationCount)
      : null;
  }

  /** Submit-to-ERP tooltip: the blocked reason when one exists, otherwise what the action does. */
  get submitToErpTooltip(): string {
    if (!this.editableDeliveryCustomerId) {
      return this.translateService.instant('AI_PARSER_SELECT_CUSTOMER_FOR_PRICE');
    }
    if (this.unresolvedPriceDeviationCount > 0) {
      return this.instantCount('AI_PARSER_RESOLVE_TO_CONTINUE', this.unresolvedPriceDeviationCount);
    }
    if (this.unoverridableFaultRowCount > 0) {
      return this.unoverridableFaultMessage;
    }
    if (this.invalidRowCount > 0 && !this.invalidRowsOverridden) {
      return this.translateService.instant(this.allowInvalidRowOverride
        ? 'AI_PARSER_SUBMIT_OVERRIDE_REQUIRED'
        : 'AI_PARSER_SUBMIT_BLOCKED_INVALID_ROWS');
    }
    if (this.metadataConfirmRequired) {
      return this.translateService.instant('AI_PARSER_METADATA_CONFIRM_REQUIRED');
    }
    if (this.unregisteredAddressConfirmRequired) {
      return this.translateService.instant('AI_PARSER_ADDRESS_UNREGISTERED_REQUIRED');
    }
    if (this.customerOverrideConfirmRequired) {
      return this.translateService.instant('AI_PARSER_CUSTOMER_OVERRIDE_REQUIRED');
    }
    return this.translateService.instant('AI_PARSER_SUBMIT_LEDE');
  }

  trackByIndex(index: number): number {
    return index;
  }

  /**
   * Track config options by name so replacing the `configs` array on save keeps the
   * selected <option> element alive. Recreating the options would drop the select's
   * DOM selection, and the deferred re-select is a no-op when the name is unchanged
   * (one-way [ngModel] sees the same value), so the dropdown would show none selected.
   */
  trackByConfigName(_index: number, config: AiOrderParserConfig): string {
    return config.name;
  }
}
